import * as types from '../mutation-types';
import JsSIP from 'jssip';
import { shallowRef, unref } from 'vue';
import ConversationApi from 'dashboard/api/conversations';
import UserPhoneCredentials from 'dashboard/api/userPhoneCredentials';
import i18n from 'dashboard/i18n';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { hasPushPermissions } from 'dashboard/helper/pushHelper';
import {
  dispatchQueueStatus,
  readQueueActions,
} from 'dashboard/helper/phoneQueueActions';
import {
  getPhoneTabId,
  getOwnClientId,
  announcePhoneOwner,
  announcePhoneReleased,
  queryPhoneOwner,
  subscribePhoneChannel,
  focusPhoneTab,
  focusPhoneTabViaExtension,
} from 'dashboard/helper/phoneConnectionChannel';
import { emitter } from 'shared/helpers/mitt';
import { useAlert } from 'dashboard/composables';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// JsSIP.debug.enable('JsSIP:*');
// JsSIP.debug.disable('JsSIP:*');
let log = () => {};
let warn = () => {};

// Cross-tab phone coordination is wired up once per page load.
let tabCoordinationStarted = false;

// The `requestConnect` that is still prewarming the mic or starting the UA, so
// a second call joins it instead of starting another connect.
let requestConnectInFlight = null;

const CAUSE_STATUS_MAP = {
  Busy: 'busy',
  Unavailable: 'unavailable',
  Rejected: 'rejected',
};

// Reconnects are budgeted until the phone registers for the first time, so a
// reload with no internet can't hammer the SIP websocket once a second forever.
// The initial connect gets a single attempt and each "Retry" from the
// connection-error dialog gets two; running out raises `connectionFailed`.
// A successful registration lifts the budget (`null`), so an already-working
// connection that drops mid-shift keeps self-healing indefinitely.
const INITIAL_CONNECT_ATTEMPTS = 1;
const RETRY_CONNECT_ATTEMPTS = 2;
// A registration the PBX actively refuses (bad extension, bad password) can't
// heal by retrying, so it gets a single attempt even on an already-registered
// connection. Without this cap such a failure would reconnect once a second
// forever, because a successful registration lifts the budget to `null`.
const AUTH_FAIL_ATTEMPTS = 1;

// JsSIP gives up on an unanswered REGISTER after its non-INVITE timer (32s) and
// emits `registrationFailed`. This watchdog sits just past that, so it only
// catches the cases JsSIP leaves hanging on `connecting` forever.
const REGISTRATION_TIMEOUT_MS = 35000;
// A REFER can be answered with a failure, or simply never produce the final
// NOTIFY that tells us how it went. Without a deadline the widget would sit in
// 'completing' with the caller on hold and no way back.
const TRANSFER_COMPLETION_TIMEOUT_MS = 30000;
// JsSIP reports every answered call as 'accepted', 'failed' or 'ended'. Past
// SIP Timer B (64*T1 = 32s) the caller's side has given up on our 200 OK, so a
// session still silent by then never will report and must not hold the phone.
const ANSWER_TIMEOUT_MS = 32000;
// JsSIP's Transactions.C.STATUS_TERMINATED. The constant is not exported.
const SIP_TRANSACTION_TERMINATED = 6;

// `registrationFailed` causes that mean "the PBX knows you and said no", as
// opposed to a transport problem. Values come from JsSIP's own `causes` /
// `SIP_ERROR_CAUSES` maps (401/407, 403/603, 404/604).
const AUTH_FAILURE_CAUSES = new Set([
  'Authentication Error',
  'Rejected',
  'Not Found',
]);
const AUTH_FAILURE_STATUS_CODES = new Set([401, 403, 404, 407]);

// Why the phone could not connect. Drives the error copy, and whether retrying
// is worth offering at all.
//   'network'       — transport/registrar unreachable; retrying can work.
//   'credentials'   — the PBX rejected the username or password.
//   'configuration' — the values can't even form a SIP URI (JsSIP threw).
export function registrationFailureReason(e) {
  const rejected =
    AUTH_FAILURE_CAUSES.has(e?.cause) ||
    AUTH_FAILURE_STATUS_CODES.has(e?.response?.status_code);
  return rejected ? 'credentials' : 'network';
}

export function isConfigurationError(e) {
  return e?.name === 'CONFIGURATION_ERROR';
}

function loadAudio(name, loop) {
  const audio = new Audio(`/audio/dashboard/${name}.mp3`);
  audio.loop = loop;

  // patch play() to comply with browser's autoplay policy
  audio.originalPlay = audio.play;
  audio.play = () => {
    const context = new AudioContext();
    if (context.state === 'running') {
      return audio.originalPlay().catch(reason => {
        log(`Failed to play ${name} audio`, reason);
      });
    }
    return Promise.resolve();
  };
  return shallowRef(audio);
}

export const state = {
  accountId: null,
  userId: null,
  userRequestedStatus: 'offline', // offline, online
  status: 'offline', // offline, connecting, connected, dialing, ringing, speaking, busy, unavailable
  pendingReconnect: false,
  // Automatic connect attempts left before we give up; null means unlimited.
  connectAttemptsLeft: null,
  // True once the budget ran out — surfaces the connection-error dialog.
  connectionFailed: false,
  // Why it failed: 'network' | 'credentials' | 'configuration'. Kept next to
  // the flag rather than folded into `status`, which is a call state machine
  // consumed by a dozen template branches in two apps.
  connectionErrorReason: null,
  registrationTimer: null,
  userAgent: null,
  session: null,
  request: null,
  sessionId: null,
  keepAliveInterval: null,
  phoneQueueStatusPollInterval: null,
  reconnectAfterCall: false,
  streams: { local: null, remote: null },
  iceConnected: false,
  callTimer: null,
  callDuration: null,
  dialingTimer: null,
  dialingDuration: null,
  ringtone: loadAudio('ringtone', true),
  busyTone: loadAudio('busy'),
  hangupDing: loadAudio('hangup'),
  connectDing: loadAudio('ding'),
  holdStatus: false,
  micMuted: false,
  speakerSilent: false,
  keepAliveResponse: null,
  inboundCallNotification: null,
  chromeExtensionAvailable: false,
  transferSession: null,
  // Sub-state of status === 'transferring'. mode picks the flow the agent
  // chose ('blind' completes as soon as the target answers, 'consult' stops
  // so the two agents can talk first); phase says where that flow stands.
  transferMode: null,
  transferPhase: null,
  transferTarget: null,
  transferRemoteStream: null,
  transferCompletionTimer: null,
  phoneControlsVisible: false,
  turnCredentials: null,
  turnCredentialsInterval: null,
  rtpStatsInterval: null,
  connectionQuality: null,
  connectionRtt: null,
  connectionJitter: null,
  connectionPacketLoss: null,
  connectionQualityReady: false,
  conversation: null,
  disconnectedAt: null,
  microphoneStream: null,
  microphoneStreamAcquiredAt: null,
  // Another tab currently holds the phone connection: { tabId, clientId } | null
  remotePhoneOwner: null,
  // This tab's service-worker client id (for other tabs to focus us) | null
  ownClientId: null,
  selectedMicrophoneId:
    LocalStorage.get(LOCAL_STORAGE_KEYS.PHONE_MIC_DEVICE) || null,
  selectedSpeakerId:
    LocalStorage.get(LOCAL_STORAGE_KEYS.PHONE_SPEAKER_DEVICE) || null,
};

const MIC_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
  },
  video: false,
};

// The persisted device id is the source of truth so that module-level helpers
// (used outside of action context) and the prewarm/session paths all agree.
function getSelectedMicId() {
  return LocalStorage.get(LOCAL_STORAGE_KEYS.PHONE_MIC_DEVICE) || null;
}

function getSelectedSpeakerId() {
  return LocalStorage.get(LOCAL_STORAGE_KEYS.PHONE_SPEAKER_DEVICE) || null;
}

const DEVICE_LABEL_KEYS = {
  audioinput: LOCAL_STORAGE_KEYS.PHONE_MIC_DEVICE_LABEL,
  audiooutput: LOCAL_STORAGE_KEYS.PHONE_SPEAKER_DEVICE_LABEL,
};

// The label of the remembered device, kept next to its id so the choice can
// outlive the id: a salted hash that changes whenever site data is cleared.
// LocalStorage.get JSON-parses, so a label like "2" comes back as a number.
export function getStoredDeviceLabel(kind) {
  const label = LocalStorage.get(DEVICE_LABEL_KEYS[kind]);
  return label === null || label === undefined || label === ''
    ? null
    : String(label);
}

// Device ids are 64-char salted hashes; logging one whole is noise. A short
// prefix is enough to tell two selections apart in [PhoneLog].
export function deviceRef(deviceId) {
  if (!deviceId) return 'system default';
  return deviceId.length > 12 ? `${deviceId.slice(0, 8)}…` : deviceId;
}

// Builds MIC_CONSTRAINTS pinned to a specific input device. `exact` rejects
// with OverconstrainedError when the device is gone (used where we can catch
// and retry); `ideal` silently degrades to the default input instead (used on
// paths that can't retry).
function buildMicConstraints(deviceId, { exact }) {
  if (!deviceId) return MIC_CONSTRAINTS;
  return {
    ...MIC_CONSTRAINTS,
    audio: {
      ...MIC_CONSTRAINTS.audio,
      deviceId: exact ? { exact: deviceId } : { ideal: deviceId },
    },
  };
}

// Returns MIC_CONSTRAINTS for JsSIP's internal getUserMedia fallback (the path
// taken when we don't hand it a prewarmed stream). Uses `ideal` so a stale or
// unplugged selected device can't throw OverconstrainedError here — JsSIP has
// no retry and relabels any getUserMedia failure as the misleading
// "User Denied Media Access".
function getMicConstraints() {
  return buildMicConstraints(getSelectedMicId(), { exact: false });
}

// Currently connected devices of one kind ('audioinput' / 'audiooutput') as
// `{ deviceId, label }`. Empty when the browser won't reveal them yet (without
// mic permission enumerateDevices returns blank ids), in which case callers
// must not treat a stored id as invalid.
export async function availableDevices(kind) {
  try {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter(d => d.kind === kind && d.deviceId)
      .map(({ deviceId, label }) => ({ deviceId, label: label || '' }));
  } catch (e) {
    log('enumerateDevices failed', e);
    return [];
  }
}

export async function availableDeviceIds(kind) {
  return (await availableDevices(kind)).map(d => d.deviceId);
}

// Chrome's "Default - X" / "Communications - X" entries: their ids never
// change, while their labels follow whatever the OS default currently is — so
// they must never take part in matching by label.
const PSEUDO_DEVICE_IDS = ['default', 'communications'];

// The single definition of "is this remembered device still meaningful". The
// `unknown` verdict matters as much as `stale`: enumeration hides ids until mic
// permission is granted, and "no ids" must mean "can't tell", never "gone" —
// otherwise a first page load would wipe a perfectly good selection.
// `relabeled` is the same device under a new id (site data cleared, id salt
// rotated), recognised by a label that exactly one connected device carries.
// `available` accepts plain ids or `{ deviceId, label }` entries.
export function resolveDeviceId(storedId, available, storedLabel = null) {
  if (!storedId) return { status: 'default', id: null };
  const devices = (available || []).map(d =>
    typeof d === 'string' ? { deviceId: d, label: '' } : d
  );
  if (devices.length === 0) return { status: 'unknown', id: storedId };
  if (devices.some(d => d.deviceId === storedId)) {
    return { status: 'ok', id: storedId };
  }
  if (storedLabel && !PSEUDO_DEVICE_IDS.includes(storedId)) {
    const sameLabel = devices.filter(
      d => d.label === storedLabel && !PSEUDO_DEVICE_IDS.includes(d.deviceId)
    );
    if (sameLabel.length === 1) {
      return { status: 'relabeled', id: sameLabel[0].deviceId };
    }
  }
  return { status: 'stale', id: null };
}

// A remembered device that is not connected is no longer forgotten, so every
// call would repeat the same warning. One line per disconnection is enough;
// seeing the device again re-arms it.
const reportedMissingDevices = new Set();

function reportMissingDevice(key, message) {
  if (reportedMissingDevices.has(key)) return;
  reportedMissingDevices.add(key);
  warn(message);
}

function trackDeviceResolution(kind, storedId, status) {
  const key = `${kind}:${storedId}`;
  if (status !== 'stale') {
    reportedMissingDevices.delete(key);
    return;
  }
  const what = kind === 'audioinput' ? 'mic' : 'speaker';
  reportMissingDevice(
    key,
    `Selected ${what} ${deviceRef(storedId)} not connected — keeping the choice, using the system default until it is back`
  );
}

const DEVICE_ID_KEYS = {
  audioinput: LOCAL_STORAGE_KEYS.PHONE_MIC_DEVICE,
  audiooutput: LOCAL_STORAGE_KEYS.PHONE_SPEAKER_DEVICE,
};

// Persists a device choice together with its label. The id is written before
// the first await, so a caller acquiring right after sees the new choice.
async function rememberDevice(kind, deviceId) {
  const idKey = DEVICE_ID_KEYS[kind];
  const labelKey = DEVICE_LABEL_KEYS[kind];
  const what = kind === 'audioinput' ? 'microphone' : 'speaker';
  if (!deviceId) {
    LocalStorage.remove(idKey);
    LocalStorage.remove(labelKey);
    log(`Picked ${what}: system default`);
    return;
  }
  const previousId = LocalStorage.get(idKey) || null;
  LocalStorage.set(idKey, deviceId);

  const device = (await availableDevices(kind)).find(
    d => d.deviceId === deviceId
  );
  // A newer choice landed while we were enumerating; its label is its own.
  if (String(LocalStorage.get(idKey) ?? '') !== deviceId) return;
  if (device?.label) {
    LocalStorage.set(labelKey, device.label);
  } else if (previousId !== deviceId) {
    // Re-applying the same (currently missing) device must keep the label it
    // was remembered by — that label is what finds it again.
    LocalStorage.remove(labelKey);
  }
  // Without this line a pick leaves no trace at all: the only device name in
  // the logs is the one actually captured, so "the agent chose the headset and
  // still went out on the laptop mic" and "the agent never chose anything" look
  // identical from here.
  log(
    `Picked ${what}: "${device?.label || getStoredDeviceLabel(kind) || 'unknown'}" ` +
      `(${deviceRef(deviceId)})`
  );
}

// Output-device counterpart of acquireMicStream's pin-drop. Returns the sink id
// to hand to setSinkId — '' meaning the system default.
export async function resolveSpeakerId(
  storedId = getSelectedSpeakerId(),
  storedLabel = getStoredDeviceLabel('audiooutput')
) {
  // Nothing remembered is already the answer — don't enumerate to learn it.
  if (!storedId) return '';
  const { status, id } = resolveDeviceId(
    storedId,
    await availableDevices('audiooutput'),
    storedLabel
  );
  trackDeviceResolution('audiooutput', storedId, status);
  return id || '';
}

// Acquires a mic stream honoring the user's selected input device without ever
// failing hard on a stale/invalid device id. The stored id is a per-origin,
// per-profile salted hash — it goes dead when site data is cleared, the profile
// changes, or the device is unplugged. We (1) pin the device under its current
// id when only the id changed (matched by label), skip pinning when enumeration
// shows it is not connected, and (2) still retry on the default input
// if getUserMedia rejects with OverconstrainedError (device lost in the race
// between enumerate and acquire, or ids hidden so we couldn't validate).
export async function acquireMicStream() {
  const storedId = getSelectedMicId();
  const { status, id: pinnedId } = resolveDeviceId(
    storedId,
    storedId ? await availableDevices('audioinput') : [],
    getStoredDeviceLabel('audioinput')
  );
  if (storedId) trackDeviceResolution('audioinput', storedId, status);
  try {
    return await navigator.mediaDevices.getUserMedia(
      buildMicConstraints(pinnedId, { exact: true })
    );
  } catch (e) {
    if (e.name === 'OverconstrainedError' && pinnedId) {
      warn(
        `Selected mic ${deviceRef(pinnedId)} unavailable — retrying default input`,
        e
      );
      return navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
    }
    throw e;
  }
}

async function applySenderOptimizations(pc) {
  const audioSender = pc.getSenders().find(s => s.track?.kind === 'audio');
  if (!audioSender) return;
  try {
    const params = audioSender.getParameters();
    if (!params.encodings || params.encodings.length === 0) {
      params.encodings = [{}];
    }
    params.encodings[0].networkPriority = 'high';
    params.encodings[0].priority = 'high';
    await audioSender.setParameters(params);
    log('Audio sender tuned: networkPriority=high');
  } catch (e) {
    log('Failed to apply sender optimizations', e);
  }
}

function serializeDetail(detail) {
  if (detail === null || detail === undefined) return detail;
  if (detail instanceof Error) {
    return { error: detail.message, stack: detail.stack };
  }
  if (typeof detail === 'object') {
    try {
      JSON.stringify(detail);
      return detail;
    } catch {
      return String(detail);
    }
  }
  return detail;
}

let cableSubscription = null;

export function setPhoneLogSubscription(subscription) {
  cableSubscription = subscription;
}

function submitLog(message, details, level = 'info') {
  try {
    if (!cableSubscription) return;
    cableSubscription.perform('phone_log', {
      account_id: state.accountId,
      user_id: state.userId,
      call_id: state.sessionId,
      level,
      log: {
        message,
        details: details.map(serializeDetail),
        timestamp: new Date().toISOString(),
      },
    });
  } catch {
    // silently ignore log delivery failures
  }
}

/* eslint-disable no-console */
log = (message, ...details) => {
  const prefix = `[SIP][account=${state.accountId}|user=${state.userId}|call=${state.sessionId}]`;
  console.log(`${prefix} ${message}`, ...details);
  submitLog(message, details);
};

// Same trail, shipped at warn. Reserved for the audio-device failures we need to
// be able to find after the fact — Rails.logger.info is sampled away by the log
// shipper, so an info-only line is not evidence of anything in production.
warn = (message, ...details) => {
  const prefix = `[SIP][account=${state.accountId}|user=${state.userId}|call=${state.sessionId}]`;
  console.warn(`${prefix} ${message}`, ...details);
  submitLog(message, details, 'warn');
};
/* eslint-enable no-console */

export function phoneLog(message, ...details) {
  log(message, ...details);
}

export function phoneWarn(message, ...details) {
  warn(message, ...details);
}

function setupPeerConnectionListeners(peerconnection, dispatch, commit) {
  let senderOptimized = false;

  // Listen for remote tracks - this is the standard WebRTC way to receive media
  peerconnection.addEventListener('track', event => {
    const t = event.track;
    log(
      `Remote track received: kind=${t.kind} id=${t.id} ` +
        `readyState=${t.readyState} enabled=${t.enabled} muted=${t.muted}`
    );
    if (t.kind === 'audio' && event.streams.length > 0) {
      dispatch('handleRemoteTrack', {
        track: t,
        streams: event.streams,
      });
      // Monitor track lifecycle — these fire if the SBC tears down the track
      t.addEventListener('ended', () => {
        log(`Remote audio track ended (id=${t.id})`);
      });
      t.addEventListener('mute', () => {
        log(`Remote audio track muted (id=${t.id})`);
      });
      t.addEventListener('unmute', () => {
        log(`Remote audio track unmuted (id=${t.id})`);
      });
    }
  });

  peerconnection.addEventListener('iceconnectionstatechange', () => {
    const iceState = peerconnection.iceConnectionState;
    log(`ICE connection state: ${iceState}`);

    if (iceState === 'connected' || iceState === 'completed') {
      dispatch('onIceConnected');
      if (!senderOptimized) {
        senderOptimized = true;
        applySenderOptimizations(peerconnection);
      }
    } else if (iceState === 'failed') {
      log('ICE connection failed - call may have audio issues');
      commit(types.default.SET_PHONE_ICE_CONNECTED, false);
      commit(types.default.SET_PHONE_CONNECTION_QUALITY, 0);
    } else if (iceState === 'disconnected') {
      log('ICE connection disconnected - may recover');
      commit(types.default.SET_PHONE_ICE_CONNECTED, false);
      commit(types.default.SET_PHONE_CONNECTION_QUALITY, 0);
    }
  });

  peerconnection.addEventListener('connectionstatechange', () => {
    log(`PeerConnection state: ${peerconnection.connectionState}`);
  });
}

async function logStallDiagnostics(pc) {
  try {
    // Track state
    const audioReceiver = pc
      .getReceivers()
      .find(r => r.track?.kind === 'audio');
    if (audioReceiver) {
      const t = audioReceiver.track;
      log(
        `Receiver track: readyState=${t.readyState} enabled=${t.enabled} muted=${t.muted}`
      );
    }
    // The send side matters just as much: a track left disabled by a previous
    // call, or muted at the OS level, is the "nobody can hear me" case.
    const senderTrack = pc
      .getSenders()
      .find(s => s.track?.kind === 'audio')?.track;
    if (senderTrack) {
      // `selected` is the agent's own choice, `label` is what the browser
      // actually opened. They disagree whenever a chosen device is gone (we
      // fall back to the system default) — which is the single most common
      // "why does the caller hear the whole office" cause, and was invisible
      // here while only the captured device was logged.
      log(
        `Sender track: selected="${getStoredDeviceLabel('audioinput') || 'system default'}" ` +
          `(${deviceRef(getSelectedMicId())}) ` +
          `label="${senderTrack.label}" readyState=${senderTrack.readyState} ` +
          `enabled=${senderTrack.enabled} muted=${senderTrack.muted}`
      );
    }
    // And the other half of "remote audio arrives but the agent hears nothing":
    // where the element is actually routing it, and whether it is even playing.
    const el = document.getElementById('phoneRemoteAudio');
    if (el) {
      log(
        `Remote audio element: sink=${deviceRef(el.sinkId)} volume=${el.volume} ` +
          `muted=${el.muted} paused=${el.paused} hasStream=${!!el.srcObject}`
      );
    }
    log(`ICE=${pc.iceConnectionState} DTLS=${pc.connectionState}`);

    // RTP, candidate pair, and candidate type detail
    const stats = await pc.getStats();
    const candidates = {};
    stats.forEach(report => {
      if (
        report.type === 'local-candidate' ||
        report.type === 'remote-candidate'
      ) {
        candidates[report.id] = report;
      }
    });
    stats.forEach(report => {
      if (report.type === 'candidate-pair' && report.state === 'succeeded') {
        const local = candidates[report.localCandidateId];
        const remote = candidates[report.remoteCandidateId];
        log(
          `Candidate pair: ` +
            `local=${local?.candidateType}(${local?.address}:${local?.port}) ` +
            `remote=${remote?.candidateType}(${remote?.address}:${remote?.port}) ` +
            `bytesSent=${report.bytesSent} bytesRecv=${report.bytesReceived}`
        );
      } else if (report.type === 'inbound-rtp' && report.kind === 'audio') {
        log(
          `Inbound RTP: packets=${report.packetsReceived} ` +
            `lost=${report.packetsLost} jitter=${report.jitter}`
        );
      } else if (report.type === 'transport') {
        log(
          `Transport: dtls=${report.dtlsState} ` +
            `selectedPair=${report.selectedCandidatePairId} ` +
            `bytesSent=${report.bytesSent} bytesRecv=${report.bytesReceived}`
        );
      } else if (report.type === 'media-playout') {
        log(
          `Playout: totalSamples=${report.totalSamplesCount} ` +
            `synthesized=${report.synthesizedSamplesDuration} ` +
            `delay=${report.totalPlayoutDelay}`
        );
      } else if (report.type === 'codec') {
        log(
          `Codec: ${report.mimeType} ` +
            `clockRate=${report.clockRate} channels=${report.channels}`
        );
      }
    });
  } catch (e) {
    log('Failed to collect stall diagnostics', e);
  }
}

function scoreMetric(value, thresholds) {
  for (let i = 0; i < thresholds.length; i += 1) {
    if (value > thresholds[i]) return i;
  }
  return thresholds.length;
}

const QUALITY_LABELS = ['Disconnected', 'Poor', 'Fair', 'Good', 'Excellent'];

function computeConnectionQuality(jitter, packetLossRate, rtt, isStalled) {
  if (isStalled) return 0;

  const jitterScore = scoreMetric(jitter, [0.1, 0.05, 0.03, 0.015]);
  const lossScore = scoreMetric(packetLossRate, [10, 5, 2, 1]);
  const rttScore = scoreMetric(rtt, [0.4, 0.25, 0.15, 0.08]);

  return Math.min(jitterScore, lossScore, rttScore);
}

function readAudioStats(stats) {
  const result = {
    bytesSent: 0,
    bytesReceived: 0,
    jitter: 0,
    packetsReceived: 0,
    packetsLost: 0,
    roundTripTime: 0,
    // Audio energy is what tells a working device apart from a dead one that is
    // still enumerated (a bluetooth headset that is paired but powered off, a
    // virtual device left behind by Krisp/Teams/VB-Cable). Bytes keep flowing
    // in that case; energy does not move at all.
    outAudioEnergy: 0,
    outAudioLevel: 0,
    inAudioEnergy: 0,
    inAudioLevel: 0,
  };
  stats.forEach(report => {
    if (report.type === 'outbound-rtp' && report.kind === 'audio') {
      result.bytesSent = report.bytesSent;
    }
    if (report.type === 'media-source' && report.kind === 'audio') {
      result.outAudioEnergy = report.totalAudioEnergy || 0;
      result.outAudioLevel = report.audioLevel || 0;
    }
    if (report.type === 'inbound-rtp' && report.kind === 'audio') {
      result.bytesReceived = report.bytesReceived;
      result.jitter = report.jitter || 0;
      result.packetsReceived = report.packetsReceived || 0;
      result.packetsLost = report.packetsLost || 0;
      result.inAudioEnergy = report.totalAudioEnergy || 0;
      result.inAudioLevel = report.audioLevel || 0;
    }
    if (
      report.type === 'candidate-pair' &&
      report.state === 'succeeded' &&
      report.currentRoundTripTime
    ) {
      result.roundTripTime = report.currentRoundTripTime;
    }
  });
  return result;
}

// A track can report `muted` for a moment while the OS hands the device over
// (a call starting elsewhere, a bluetooth codec switch). Only a mute that
// outlives this is treated as capture actually being gone.
const MIC_MUTE_SETTLE_MS = 2000;

// Ticks are one second. A short grace covers call setup, and 15s of continuous
// digital silence is well past anything a working microphone produces.
const MIC_SILENCE_GRACE_TICKS = 3;
const MIC_SILENCE_WARN_TICKS = 15;

function getSenderAudioTrack(pc) {
  return pc?.getSenders?.().find(s => s.track?.kind === 'audio')?.track || null;
}

// Deliberately an exact-zero test rather than a threshold. A live microphone in
// a silent room still accumulates a tiny but non-zero totalAudioEnergy every
// second (~1e-9 and up), even with noise suppression on; a device that is dead,
// OS-muted, or a virtual sink with nothing behind it accumulates exactly none.
// That makes this a near-zero-false-positive test on a merely quiet operator.
export function isCapturingSilence({ energyDelta, level, trackMuted }) {
  if (trackMuted === true) return true;
  return energyDelta === 0 && level === 0;
}

function warnMicrophoneIsSilent(track) {
  warn(
    `Microphone is producing digital silence for ${MIC_SILENCE_WARN_TICKS}s ` +
      `(device=${deviceRef(getSelectedMicId())} track="${track?.label}" ` +
      `muted=${track?.muted} readyState=${track?.readyState})`
  );
  useAlert(i18n.global.t('PHONE_AUDIO.ERRORS.MICROPHONE_SILENT'));
  // Only pop the settings panel when the device is unambiguously gone. The
  // dashboard popup is a full-screen fixed overlay — opening it mid-call over a
  // recoverable warning would bury the hangup button.
  if (track?.readyState === 'ended') {
    emitter.emit(BUS_EVENTS.SHOW_PHONE_AUDIO_SETTINGS);
  }
}

function startRtpStatsMonitor(session, commit) {
  let prevBytesReceived = 0;
  let prevBytesSent = 0;
  let prevPacketsReceived = 0;
  let prevPacketsLost = 0;
  let prevOutAudioEnergy = 0;
  let prevInAudioEnergy = 0;
  let stallCount = 0;
  let micSilenceTicks = 0;
  let micSilenceWarned = false;
  let tick = 0;
  let totalTicks = 0;
  let prevQuality = null;
  return setInterval(async () => {
    if (!session?.connection) return;
    try {
      const {
        bytesSent,
        bytesReceived,
        jitter,
        packetsReceived,
        packetsLost,
        roundTripTime,
        outAudioEnergy,
        outAudioLevel,
        inAudioEnergy,
        inAudioLevel,
      } = readAudioStats(await session.connection.getStats());
      tick += 1;
      totalTicks += 1;

      const outEnergyDelta = outAudioEnergy - prevOutAudioEnergy;
      const inEnergyDelta = inAudioEnergy - prevInAudioEnergy;

      if (tick >= 10) {
        // Per-tick deltas, not running totals: bytesSent/bytesReceived are
        // cumulative counters, so summing them once a second grew this line
        // quadratically and made it useless for judging whether media flowed.
        log(
          `Audio out: ${bytesSent - prevBytesSent} bytes/s ` +
            `(energy+${outEnergyDelta.toExponential(2)} level=${outAudioLevel}), ` +
            `Audio in: ${bytesReceived - prevBytesReceived} bytes/s ` +
            `(energy+${inEnergyDelta.toExponential(2)} level=${inAudioLevel})`
        );
        tick = 0;
      }

      const inboundStalled = bytesReceived === prevBytesReceived;
      const outboundStalled = bytesSent === prevBytesSent;

      // A microphone that captures nothing at all: the device is enumerated and
      // the track is live, so nothing else on this path can notice. Suppressed
      // only while muted or on hold, where silence is the point — audio energy
      // is measured at the local source, so a network stall keeps it climbing
      // and can't be mistaken for a dead device.
      const senderTrack = getSenderAudioTrack(session.connection);
      const micSilent =
        totalTicks > MIC_SILENCE_GRACE_TICKS &&
        !state.micMuted &&
        !state.holdStatus &&
        isCapturingSilence({
          energyDelta: outEnergyDelta,
          level: outAudioLevel,
          trackMuted: senderTrack?.muted,
        });
      micSilenceTicks = micSilent ? micSilenceTicks + 1 : 0;
      if (micSilenceTicks >= MIC_SILENCE_WARN_TICKS && !micSilenceWarned) {
        micSilenceWarned = true;
        warnMicrophoneIsSilent(senderTrack);
      }

      if (inboundStalled || outboundStalled) {
        stallCount += 1;
        // Log diagnostics once on first detection
        if (stallCount === 5) {
          log(
            `Audio stall: inbound=${inboundStalled ? 'STALLED' : 'ok'} ` +
              `outbound=${outboundStalled ? 'STALLED' : 'ok'}`
          );
          logStallDiagnostics(session.connection);
        }
        if (stallCount % 5 === 0) {
          log('Audio stall persists — triggering renegotiation');
          session.renegotiate({
            rtcOfferConstraints: { iceRestart: true },
          });
        }
      } else {
        if (stallCount >= 5) {
          log('Audio recovered after stall');
        }
        stallCount = 0;
      }

      const deltaPackets = packetsReceived - prevPacketsReceived;
      const deltaLost = packetsLost - prevPacketsLost;
      const packetLossRate =
        deltaPackets + deltaLost > 0
          ? (deltaLost / (deltaPackets + deltaLost)) * 100
          : 0;
      const quality = computeConnectionQuality(
        jitter,
        packetLossRate,
        roundTripTime,
        stallCount >= 3
      );

      if (prevQuality !== null && quality !== prevQuality) {
        const metrics = [
          `jitter=${Math.round(jitter * 1000)}ms`,
          `packetLoss=${Math.round(packetLossRate * 10) / 10}%`,
          `rtt=${Math.round(roundTripTime * 1000)}ms`,
        ].join(', ');

        if (quality === 0) {
          state.disconnectedAt = new Date();
          // eslint-disable-next-line no-console
          console.log(
            `[SIP][account=${state.accountId}|user=${state.userId}|call=${state.sessionId}] ` +
              `Connection quality dropped to Disconnected (was ${QUALITY_LABELS[prevQuality]}) — ${metrics}`
          );
        } else if (prevQuality === 0 && state.disconnectedAt) {
          const duration = Math.round(
            (new Date() - state.disconnectedAt) / 1000
          );
          log(
            `Connection recovered to ${QUALITY_LABELS[quality]} after ${duration}s disconnected`,
            metrics
          );
          state.disconnectedAt = null;
        } else {
          log(
            `Connection quality changed: ${QUALITY_LABELS[prevQuality]} → ${QUALITY_LABELS[quality]}`,
            metrics
          );
        }
      }
      prevQuality = quality;

      commit(types.default.SET_PHONE_CONNECTION_QUALITY, quality);
      commit(
        types.default.SET_PHONE_CONNECTION_RTT,
        Math.round(roundTripTime * 1000)
      );
      commit(
        types.default.SET_PHONE_CONNECTION_JITTER,
        Math.round(jitter * 1000)
      );
      commit(
        types.default.SET_PHONE_CONNECTION_PACKET_LOSS,
        Math.round(packetLossRate * 10) / 10
      );

      prevBytesSent = bytesSent;
      prevBytesReceived = bytesReceived;
      prevPacketsReceived = packetsReceived;
      prevPacketsLost = packetsLost;
      prevOutAudioEnergy = outAudioEnergy;
      prevInAudioEnergy = inAudioEnergy;
    } catch (mediaErr) {
      log('Media check failed, session is probably over.', mediaErr);
    }
  }, 1000);
}

// Connection quality readings are noisy in the first moments of a call while
// ICE negotiation and codec setup settle. The quality indicator stays grey for
// QUALITY_WARMUP_MS, then reveals the real value — unless the link is already
// excellent and holds steady for QUALITY_EARLY_REVEAL_MS, in which case it is
// revealed early. Sampling runs faster than the main monitor so the early
// reveal can actually beat the warm-up cap.
const QUALITY_WARMUP_MS = 2000;
const QUALITY_WARMUP_SAMPLE_MS = 250;
const QUALITY_EARLY_REVEAL_MS = 1000;
const EXCELLENT_QUALITY = 4;

function startConnectionQualityWarmup(session, commit) {
  const startedAt = Date.now();
  let excellentSince = null;
  let prevPacketsReceived = 0;
  let prevPacketsLost = 0;
  let finished = false;
  let timer = null;

  const finish = () => {
    if (finished) return;
    finished = true;
    clearInterval(timer);
    // Guard against a call that already ended (or was replaced) mid warm-up.
    // state.session is a shallowRef on the raw state object, so unwrap it.
    if (unref(state.session) === session) {
      commit(types.default.SET_PHONE_CONNECTION_QUALITY_READY, true);
    }
  };

  timer = setInterval(async () => {
    if (Date.now() - startedAt >= QUALITY_WARMUP_MS) {
      finish();
      return;
    }
    if (unref(state.session) !== session || !session?.connection) return;
    try {
      const { jitter, packetsReceived, packetsLost, roundTripTime } =
        readAudioStats(await session.connection.getStats());
      const deltaPackets = packetsReceived - prevPacketsReceived;
      const deltaLost = packetsLost - prevPacketsLost;
      const packetLossRate =
        deltaPackets + deltaLost > 0
          ? (deltaLost / (deltaPackets + deltaLost)) * 100
          : 0;
      prevPacketsReceived = packetsReceived;
      prevPacketsLost = packetsLost;
      const quality = computeConnectionQuality(
        jitter,
        packetLossRate,
        roundTripTime,
        false
      );
      if (quality >= EXCELLENT_QUALITY) {
        if (excellentSince === null) {
          excellentSince = Date.now();
        } else if (Date.now() - excellentSince >= QUALITY_EARLY_REVEAL_MS) {
          finish();
        }
      } else {
        excellentSince = null;
      }
    } catch (warmupErr) {
      log('Connection quality warm-up sample failed', warmupErr);
    }
  }, QUALITY_WARMUP_SAMPLE_MS);
}

function setupSessionListeners(session, dispatch, commit, _state) {
  session.on('sdp', e => {
    log(`SDP ${e.originator} (type=${e.type})`, e.sdp?.substring(0, 200));
  });
  session.on('reinvite', () => {
    log('Received re-INVITE from remote');
  });
  session.on('update', () => {
    log('Received SIP UPDATE from remote');
  });
  session.on('icecandidate', e => {
    log(`ICE candidate: ${e.candidate ? e.candidate.candidate : 'null'}`);
  });

  // For incoming calls, RTCPeerConnection is created during answer()
  // We need to listen for the peerconnection event to get access to it
  session.on('peerconnection', ({ peerconnection }) => {
    log('PeerConnection created');
    setupPeerConnectionListeners(peerconnection, dispatch, commit);
  });

  // For outgoing calls, the connection may already exist
  if (session.connection) {
    setupPeerConnectionListeners(session.connection, dispatch, commit);
  }

  session.on('progress', async e => {
    log('Call progress', e);
  });
  session.on('ended', async e => {
    log('Call ended', e);
    await dispatch('callEnded', { reason: 'hangup' });
  });
  session.on('failed', async e => {
    log('Call failed', e.originator, e.cause);
    await dispatch('callEnded', { reason: 'failed', cause: e.cause });
  });
  session.on('accepted', async e => {
    log('Call accepted', e);
    commit(types.default.SET_PHONE_STATUS, 'speaking');
    if (e?.response) {
      commit(types.default.SET_PHONE_SESSION_ID, {
        sessionId: e.response.getHeader('X-Session-ID'),
      });
    }
    await dispatch('clearInboundCallNotification');
    await dispatch('attachLocalMediaStream');
    await dispatch('callStarted');
    await dispatch('setupConversation', { attempt: 1 });
  });
  // confirmed event fires after the SIP 3-way handshake is complete
  session.on('confirmed', async () => {
    log('Call confirmed (SIP handshake complete)');
    // If we haven't received remote track yet, try to attach streams as fallback
    if (!_state.streams.remote) {
      log('No remote stream yet at confirmed, attempting fallback attachment');
      await dispatch('attachRemoteMediaStreamFallback');
    }
  });
  session.on('hold', e => {
    log(
      e.originator === 'local'
        ? 'Call put on hold'
        : 'Call put on hold by remote party'
    );
    commit(types.default.SET_PHONE_HOLD, e.originator);
  });
  session.on('unhold', e => {
    log(
      e.originator === 'local'
        ? 'Call resumed from hold'
        : 'Call resumed from hold by remote party'
    );
    commit(types.default.SET_PHONE_HOLD, false);
  });
}

function keepAliveSendOptions(_state, commit) {
  const sipUrl = _state.userAgent.configuration.uri;
  _state.userAgent.sendOptions(sipUrl, null, {
    eventHandlers: {
      succeeded: () => {
        commit(types.default.SET_PHONE_KEEPALIVE_RESPONSE, Date.now());
      },
      failed: () => {
        commit(types.default.SET_PHONE_KEEPALIVE_RESPONSE, Date.now());
      },
    },
  });
}

function parseSipContact(request) {
  try {
    return request.parseHeader('Contact')?.uri?.user;
  } catch {
    return null;
  }
}

function parseSipRequestHeader(request, header) {
  const value = request.getHeader(header);
  if (!value) return null;
  try {
    const parsed = JsSIP.Grammar.parse(value, 'From');
    return parsed?.uri?.user;
  } catch {
    return null;
  }
}

function parseCallInfo(request) {
  if (!request) return {};
  try {
    let callInfo = request.getHeader('X-Call-Info');
    if (!callInfo) {
      return {};
    }
    return JSON.parse(callInfo);
  } catch (e) {
    log('Error parsing call info', e);
    return {};
  }
}

function parseRemotePartyNumber(callInfo, session, request) {
  return (
    callInfo?.caller_number ||
    parseSipContact(request) ||
    parseSipRequestHeader(request, 'P-Asserted-Identity') ||
    parseSipRequestHeader(request, 'Remote-Party-ID') ||
    session.remote_identity.uri.user
  );
}

function parseCallerId(session) {
  const value = session.remote_identity?.display_name;

  if (value?.includes('|')) {
    return value.replace('|', '\n');
  }

  return value;
}

async function conversationFromSessionId(sessionId) {
  if (!sessionId) {
    return null;
  }
  const { data: response } = await ConversationApi.search({
    identifier: sessionId,
  });
  if (!response.data?.payload?.length) {
    return null;
  }
  return response.data.payload[0];
}

function showInboundCallPushNotification(callerNumber, callerName) {
  if (!hasPushPermissions()) {
    return null;
  }
  const title = i18n.global.t('PHONE_CTRLS.NOTIFICATIONS.INCOMING_CALL');
  const body = callerName
    ? i18n.global.t('PHONE_CTRLS.NOTIFICATIONS.INCOMING_CALL_FROM', {
        name: callerName,
        number: callerNumber,
      })
    : i18n.global.t('PHONE_CTRLS.NOTIFICATIONS.INCOMING_CALL_FROM_NUMBER', {
        number: callerNumber,
      });
  const options = {
    body: body,
    icon: '/android-chrome-512x512.png',
    tag: 'inbound-call',
  };
  return new Notification(title, options);
}

function showInboundCallExtensionNotification(
  callerId,
  callerNumber,
  callerName
) {
  window.postMessage(
    {
      type: 'INCOMING_CALL_NOTIFICATION',
      payload: {
        title: i18n.global.t('PHONE_CTRLS.NOTIFICATIONS.INCOMING_CALL'),
        callerNumber,
        callerId,
        callerName,
        acceptButtonText: i18n.global.t('PHONE_CTRLS.NOTIFICATIONS.ANSWER'),
        declineButtonText: i18n.global.t('PHONE_CTRLS.NOTIFICATIONS.DECLINE'),
        locale: i18n.global.locale.value,
      },
    },
    '*'
  );
}

// The conversation of a call can be resolved after that call ended (see the
// retries in setupConversation), so the one in the store may be left over from
// the previous call.
function conversationOfCurrentCall(_state, conversation) {
  if (!conversation) return false;
  if (_state.sessionId && conversation.identifier === _state.sessionId) {
    return true;
  }
  const callInfoConversationId = _state.callInfo?.conversation_id;
  return (
    !!callInfoConversationId &&
    String(callInfoConversationId) === String(conversation.id)
  );
}

function clearTransferState(commit) {
  commit(types.default.SET_PHONE_TRANSFER_STATE, {
    mode: null,
    phase: null,
    target: null,
  });
  commit(types.default.SET_PHONE_TRANSFER_REMOTE_STREAM, null);
  commit(types.default.SET_PHONE_TRANSFER_TIMER, null);
}

function terminateSafe(session, msg) {
  try {
    session.terminate();
  } catch (e) {
    if (msg) {
      log(msg, e);
    }
  }
}

// When the SIP websocket drops, JsSIP kills every pending INVITE transaction
// but does not tell the ringing session. answer() then hands its 200 OK to the
// dead transaction, which drops it without calling back, and the session sits
// "answered" for good: every later call is refused as already in progress.
function watchIncomingTransaction(session, request, dispatch) {
  const transaction = request?.server_transaction;
  if (!transaction?.on) return;

  let accepted = false;
  session.on('accepted', () => {
    accepted = true;
  });
  transaction.on('stateChanged', () => {
    if (transaction.state !== SIP_TRANSACTION_TERMINATED) return;
    // After a 200 OK the transaction ends on its own timer; that is normal.
    if (accepted || session.isEnded()) return;
    warn('Incoming call transaction ended before the call was accepted');
    dispatch('abandonSession', { session });
  });
}

// Backstop for any other way answer() can stall, e.g. ICE gathering that never
// completes: JsSIP has no timeout of its own there.
function watchAnswer(session, dispatch) {
  let timer = null;
  const settle = () => clearTimeout(timer);
  ['accepted', 'failed', 'ended'].forEach(e => session.on(e, settle));
  timer = setTimeout(() => {
    if (session.isEnded()) return;
    warn(`Call not accepted ${ANSWER_TIMEOUT_MS / 1000}s after answering`);
    dispatch('abandonSession', { session });
  }, ANSWER_TIMEOUT_MS);
}

// Process phone number for SIP compatibility
// Replace + with 00 for international calls, but preserve special prefixes like *23+
function cleanupOutboundNumber(number) {
  let processedNumber = number;

  // remove spaces, dashes, parentheses
  processedNumber = processedNumber.replace(/[\s-()/]+/g, '');

  if (processedNumber.includes('+')) {
    // Check if it's a special feature code like *23+number
    const featureCodeMatch = processedNumber.match(/^(\*\d+)\+(.+)$/);
    if (featureCodeMatch) {
      // For feature codes, replace + with 00
      processedNumber = featureCodeMatch[1] + '00' + featureCodeMatch[2];
    }
  }
  return processedNumber;
}

// The transfer target takes over the conversation and gets it opened.
function reportTransfer(_state) {
  const conversation = _state.conversation;
  const target = _state.transferTarget;
  if (!target || !conversationOfCurrentCall(_state, conversation)) return;
  ConversationApi.phoneTransfer(
    conversation.id,
    cleanupOutboundNumber(target)
  ).catch(error => log('Failed to report the transfer', error));
}

export const getters = {
  getSession($state) {
    return $state.session;
  },
  getStreams($state) {
    return $state.streams;
  },
  getUserStatus($state) {
    return $state.userRequestedStatus;
  },
  getStatus($state) {
    return $state.status;
  },
  getCallDuration($state) {
    return $state.callDuration;
  },
  getDialingDuration($state) {
    return $state.dialingDuration;
  },
  getHoldStatus($state) {
    return $state.holdStatus;
  },
  getTransferMode($state) {
    return $state.transferMode;
  },
  getTransferPhase($state) {
    return $state.transferPhase;
  },
  getTransferTarget($state) {
    return $state.transferTarget;
  },
  getTransferRemoteStream($state) {
    return $state.transferRemoteStream;
  },
  getMicMuted($state) {
    return $state.micMuted;
  },
  getSelectedMicrophoneId($state) {
    return $state.selectedMicrophoneId;
  },
  getSelectedSpeakerId($state) {
    return $state.selectedSpeakerId;
  },
  getSpeakerSilent($state) {
    return $state.speakerSilent;
  },
  getChromeExtensionAvailable($state) {
    return $state.chromeExtensionAvailable;
  },
  getIceConnected($state) {
    return $state.iceConnected;
  },
  getConnectionQuality($state) {
    return $state.connectionQuality;
  },
  getConnectionRtt($state) {
    return $state.connectionRtt;
  },
  getConnectionJitter($state) {
    return $state.connectionJitter;
  },
  getConnectionPacketLoss($state) {
    return $state.connectionPacketLoss;
  },
  getConnectionQualityReady($state) {
    return $state.connectionQualityReady;
  },
  getPhoneControlsVisible($state) {
    return $state.phoneControlsVisible;
  },
  getConnectionFailed($state) {
    return $state.connectionFailed;
  },
  getConnectionErrorReason($state) {
    return $state.connectionErrorReason;
  },
  getConversation($state) {
    return $state.conversation;
  },
  // { tabId, clientId } when another browser tab holds the phone, else null.
  // clientId is only set when that tab is focusable via the service worker.
  getRemotePhoneOwner($state) {
    return $state.remotePhoneOwner;
  },
  available($state, $getters, rootState, rootGetters) {
    const account = rootGetters['accounts/getCurrentAccount'];
    return (
      account.sip_host &&
      account.sip_host.length !== 0 &&
      Boolean(window.chatwootConfig?.pbxWsUrl)
    );
  },
  configured($state, $getters, rootState, rootGetters) {
    if (!$getters.available) {
      return false;
    }
    let { username, password } =
      rootGetters['userPhoneCredentials/getCredentials'];
    return username && password && username.length > 0 && password.length > 0;
  },
};

function isStreamUsable(stream) {
  if (!stream) return false;
  const tracks = stream.getAudioTracks();
  return tracks.length > 0 && tracks.every(t => t.readyState === 'live');
}

// `live` is not the same as "delivering audio". A track whose capture died —
// the machine slept, the headset went away, Chrome's audio service restarted
// underneath us — stays `live` and goes `muted` instead, and an agent who
// leaves the tab open for days will hit that. Reusing such a track is exactly
// the "nobody can hear me, a browser restart fixes it" report: it never
// recovers on its own, while a fresh getUserMedia does.
function isStreamHealthy(stream) {
  if (!isStreamUsable(stream)) return false;
  return stream.getAudioTracks().every(t => !t.muted);
}

// The prewarmed stream is deliberately kept alive across calls, and JsSIP mutes
// it in place: both session.mute() and hold set `sender.track.enabled = false`
// on these very tracks. callEnded re-enables them, but it doesn't run on every
// teardown (tab reload mid-call, UA dropped without an ended/failed event), and
// a track left disabled is still `live` — so it sails through isStreamUsable
// and the next call goes out silent with no error anywhere. Re-enable at the
// moment the stream is handed to a session, which is the one point every call
// passes through.
function rearmMicrophoneStream(stream) {
  stream.getAudioTracks().forEach(track => {
    if (!track.enabled) {
      warn('Re-enabling a microphone track left disabled by a previous call');
      track.enabled = true;
    }
    if (track.muted) {
      log(`Microphone track "${track.label}" is muted at the device level`);
    }
  });
}

function getSessionOptions(turnCredentials, microphoneStream) {
  const options = {
    mediaConstraints: getMicConstraints(),
  };

  // A live stream goes into the call even when its track reports `muted`. That
  // flag is not proof of a dead mic: some USB headsets (a Jabra, in 4camping's
  // case) report it right after opening or while idle. Rejecting it let JsSIP
  // run its own getUserMedia with only an `ideal` deviceId, and Chrome opened
  // the laptop mic instead of the chosen headset on every call. A mic that is
  // truly silent is still caught in the call by the silence detector.
  if (isStreamUsable(microphoneStream)) {
    rearmMicrophoneStream(microphoneStream);
    options.mediaStream = microphoneStream;
    // Stream age is the one number that tells a two-day-old capture apart from
    // a fresh one when reading [PhoneLog] after the fact.
    const ageMinutes = Math.round(
      (Date.now() - (state.microphoneStreamAcquiredAt || Date.now())) / 60000
    );
    const track = microphoneStream.getAudioTracks()[0];
    log(
      `Using prewarmed microphone "${track?.label}" acquired ${ageMinutes}min ago` +
        (track?.muted ? ' (reported muted — kept, the chosen device wins)' : '')
    );
  } else if (microphoneStream) {
    // Handing JsSIP no mediaStream makes it run getUserMedia itself: a fresh
    // capture, since this one has ended (device unplugged, permission revoked).
    warn('Prewarmed microphone has ended — acquiring a fresh one');
  }

  if (turnCredentials) {
    options.pcConfig = {
      iceServers: [
        {
          urls: turnCredentials.urls,
          username: turnCredentials.username,
          credential: turnCredentials.credential,
        },
      ],
      // for testing TURN server only routes
      // iceTransportPolicy: 'relay',
    };
  }
  return options;
}

// The part of `requestConnect` that runs under its lock: prewarm the mic and
// start the UA. Returns whether `connect` ran.
async function prewarmAndConnect(
  { state: _state, commit, dispatch, rootGetters },
  { attempts = INITIAL_CONNECT_ATTEMPTS } = {}
) {
  // Confirm mic access before going online. If we can't get the stream the
  // agent would register but never be heard on a call, so refuse to connect.
  // The extension is what grants mic access, so when it's missing point the
  // operator at that rather than at browser settings.
  const micReady = await dispatch('prewarmMic');
  if (!micReady) {
    useAlert(
      i18n.global.t(
        _state.chromeExtensionAvailable
          ? 'PHONE_AUDIO.ERRORS.MICROPHONE_ACCESS'
          : 'PHONE_AUDIO.ERRORS.EXTENSION_REQUIRED'
      )
    );
    // Mic is unrecoverably blocked (permission denied) — open audio settings
    // so the operator lands on the permission banner and device pickers
    // instead of just a transient toast.
    emitter.emit(BUS_EVENTS.SHOW_PHONE_AUDIO_SETTINGS);
    return false;
  }
  commit(types.default.SET_USER_PHONE_STATUS, 'online');
  sessionStorage.setItem(
    SESSION_STORAGE_KEYS.USER_REQUESTED_PHONE_STATUS(
      rootGetters.getCurrentAccountId
    ),
    'online'
  );
  // This tab now owns the phone — clear any remote owner locally first.
  commit(types.default.SET_PHONE_REMOTE_OWNER, null);
  commit(types.default.SET_PHONE_CONNECTION_FAILED, false);
  commit(types.default.SET_PHONE_CONNECTION_ERROR_REASON, null);
  commit(types.default.SET_PHONE_CONNECT_ATTEMPTS_LEFT, attempts);
  await dispatch('connect');
  return true;
}

// Tells the other tabs that this one holds the phone.
async function claimPhone({ state: _state, commit, rootGetters }) {
  // `connect` now reports a configuration failure instead of throwing, so this
  // runs where it used to be skipped. Claiming ownership of a phone that never
  // started would leave every other tab showing "connected in another tab"
  // with no way to connect.
  if (_state.connectionFailed) return;
  // Announce ownership once connected, with our resolved service-worker client
  // id so other tabs can offer a "go to that tab" button. Done after connect
  // so a first-load client-id lookup never delays connecting.
  const clientId = await getOwnClientId();
  commit(types.default.SET_PHONE_OWN_CLIENT_ID, clientId);
  announcePhoneOwner(rootGetters.getCurrentAccountId, clientId);
}

export const actions = {
  connect: async function connect({
    state: _state,
    dispatch,
    commit,
    rootGetters,
  }) {
    // One UA per tab. A second UA registers its own SIP contact, and nothing
    // can stop it: `disconnect` and `cleanup` only see the UA in the store. The
    // PBX then keeps the agent registered, and READY in the queues, after they
    // disconnect. Every reconnect goes through `cleanup`, which clears the UA
    // and connects again via `reconnectIfNeeded` while the agent wants to be
    // online.
    if (_state.userAgent) {
      log('connect: a user agent already exists - skipping');
      return;
    }
    if (_state.connectAttemptsLeft !== null) {
      commit(
        types.default.SET_PHONE_CONNECT_ATTEMPTS_LEFT,
        Math.max(0, _state.connectAttemptsLeft - 1)
      );
    }
    const accountId = rootGetters.getCurrentAccountId;
    _state.accountId = accountId;
    _state.userId = rootGetters.getCurrentUserID;
    let account = rootGetters['accounts/getAccount'](accountId);
    let creds = rootGetters['userPhoneCredentials/getCredentials'];
    dispatch('checkChromeExtension');

    // Everything from here to `ua.start()` can throw: JsSIP validates the
    // socket url and the SIP uri up front and raises rather than reporting the
    // problem through an event. An agent whose SIP username holds an email
    // address produces `sip:someone@example.com@host`, which JsSIP rejects with
    // a ConfigurationError. Uncaught, that rejection escaped through
    // `requestConnect` and the mount hooks and left the widget mute — no
    // status change, no dialog, nothing in the CTI panel (which has neither
    // Sentry nor toasts). Catch it here so every caller is covered at once.
    try {
      const sipUri = `sip:${creds.username}@${account.sip_host}`;
      const socket = new JsSIP.WebSocketInterface(
        window.chatwootConfig.pbxWsUrl
      );
      const configuration = {
        sockets: [socket],
        uri: sipUri,
        password: creds.password,
        session_timers: true,
        session_timers_refresh_method: 'update',
      };

      const ua = new JsSIP.UA(configuration);
      await commit(types.default.SET_PHONE_UA, ua);

      ua.on('connecting', data => {
        const attempts = data?.attempts || 0;
        log(
          `Connecting to SIP server${attempts > 0 ? ` (reconnect attempt ${attempts})` : ''}`
        );
      });
      ua.on('connected', () => {
        log('Connected to SIP server');
        _state.connectDing.play();
      });
      ua.on('disconnected', data => {
        log(
          'Disconnected from SIP server',
          data?.error ? `error: ${data.code} ${data.reason}` : ''
        );
        dispatch('disconnected');
      });
      ua.on('registered', () => {
        log('Registered with SIP server');
        commit(types.default.SET_PHONE_STATUS, 'connected');
        commit(types.default.SET_PHONE_RECONNECT_AFTER_CALL, false);
        // The link works — from here on reconnects are unlimited again.
        commit(types.default.SET_PHONE_CONNECT_ATTEMPTS_LEFT, null);
        commit(types.default.SET_PHONE_CONNECTION_FAILED, false);
        commit(types.default.SET_PHONE_CONNECTION_ERROR_REASON, null);
        dispatch('registrationWatchdogStop');
        dispatch('keepAliveStart');
        dispatch('audioCheckStart');
        dispatch('turnCredentialsStart');
        dispatch('phoneStatusPollStart');
        dispatch('replayLastQueueAction');
        window.postMessage(
          { type: 'PHONE_CONNECTED', timestamp: Date.now() },
          '*'
        );
      });
      ua.on('unregistered', () => {
        log('Unregistered from SIP server');
        commit(types.default.SET_PHONE_STATUS, 'connecting');
        // JsSIP won't re-register by itself, so this is a dead end unless the
        // watchdog is watching it.
        dispatch('registrationWatchdogStart');
      });
      ua.on('registrationFailed', e => {
        const reason = registrationFailureReason(e);
        log(
          `Registration failed: ${e?.cause ?? ''} ${e?.response?.status_code ?? ''} (${reason})`
        );
        commit(types.default.SET_PHONE_CONNECTION_ERROR_REASON, reason);
        // A registrar that actively refuses us won't change its mind on the
        // next attempt. Cap the budget an earlier successful registration
        // lifted, so this stops instead of retrying every second forever.
        if (reason === 'credentials' && _state.connectAttemptsLeft === null) {
          commit(
            types.default.SET_PHONE_CONNECT_ATTEMPTS_LEFT,
            AUTH_FAIL_ATTEMPTS
          );
        }
        dispatch('disconnect');
      });
      ua.on('newRTCSession', async data => {
        const { session, request } = data;
        if (
          _state.session &&
          session.direction === 'outgoing' &&
          _state.status === 'transferring'
        ) {
          log('Transfer session started.');
          dispatch('setupTransferSession', { session });
          return;
        }
        if (_state.session) {
          warn('A session is already active, terminating new session');
          terminateSafe(session, 'Failed terminating new session');
          return;
        }

        log('New RTC session', session, request);
        setupSessionListeners(session, dispatch, commit, _state);
        if (session.direction === 'incoming') {
          watchIncomingTransaction(session, request, dispatch);
        }
        await commit(types.default.SET_PHONE_SESSION, { session, request });
        await dispatch('findContact');
        if (!_state.session) {
          log('Session ended before setup completed');
          return;
        }
        if (session.direction === 'incoming') {
          await commit(types.default.SET_PHONE_STATUS, 'ringing');
          _state.ringtone.currentTime = 0;
          _state.ringtone.play();
          await dispatch('showInboundCallPushNotification');
        } else {
          await commit(types.default.SET_PHONE_STATUS, 'dialing');
          await dispatch('dialingStarted');
          log('Setting up outgoing call');
        }
      });

      commit(types.default.SET_PHONE_STATUS, 'connecting');
      log('Starting user agent');
      ua.start();
      dispatch('registrationWatchdogStart');
    } catch (e) {
      log(
        `Connect failed: ${e?.name} ${e?.parameter ?? ''} ${e?.message ?? ''}`
      );
      const reason = isConfigurationError(e) ? 'configuration' : 'network';
      commit(types.default.SET_PHONE_CONNECTION_ERROR_REASON, reason);
      // Nothing about a malformed uri improves in a second, so spend the whole
      // budget: `reconnectIfNeeded` then raises the dialog instead of looping.
      commit(types.default.SET_PHONE_CONNECT_ATTEMPTS_LEFT, 0);
      if (_state.userAgent) {
        // `ua.start()` was what threw — there is a UA to tear down, and its
        // `disconnected` event drives `cleanup` -> `reconnectIfNeeded`.
        await dispatch('disconnect');
      } else {
        // The throw came from the constructor: no UA, so no event will ever
        // arrive to move us on. Land on the failed state directly.
        commit(types.default.SET_PHONE_STATUS, 'offline');
        commit(types.default.SET_PENDING_RECONNECT, false);
        commit(types.default.SET_PHONE_CONNECTION_FAILED, true);
      }
    }
  },
  // Nothing drives the `connecting` state forward on its own: the keep-alive
  // watchdog only starts once registration succeeds, so a REGISTER that is
  // never answered used to leave the widget on "Connecting…" indefinitely.
  registrationWatchdogStart: async function registrationWatchdogStart({
    dispatch,
    commit,
  }) {
    const timer = setTimeout(
      () => dispatch('registrationTimedOut'),
      REGISTRATION_TIMEOUT_MS
    );
    commit(types.default.SET_PHONE_REGISTRATION_TIMER, timer);
  },
  registrationWatchdogStop: async function registrationWatchdogStop({
    commit,
  }) {
    commit(types.default.SET_PHONE_REGISTRATION_TIMER, null);
  },
  registrationTimedOut: async function registrationTimedOut({
    state: _state,
    dispatch,
    commit,
  }) {
    // Second line of defence against a leaked timer: only act if we are still
    // waiting to register and the agent still wants to be online.
    if (_state.status !== 'connecting') return;
    if (_state.userRequestedStatus !== 'online') return;

    log('Registration timed out');
    commit(types.default.SET_PHONE_CONNECTION_ERROR_REASON, 'network');
    await dispatch('disconnect');
  },
  keepAliveStart: async function keepAliveStart({
    state: _state,
    dispatch,
    commit,
  }) {
    if (!_state.userAgent) {
      log('Cannot start keepalive - user agent not initialized');
      return;
    }
    keepAliveSendOptions(_state, commit);
    const interval = setInterval(async () => {
      await dispatch('keepAliveCheck', { interval });
    }, 2_000);
    commit(types.default.SET_PHONE_KEEPALIVE_INTERVAL, interval);
  },
  keepAliveCheck: async function keepAliveCheck(
    { state: _state, dispatch, commit },
    { interval }
  ) {
    if (_state.keepAliveInterval !== interval) {
      log('Obsolete keepalive check.');
      clearInterval(interval);
    } else if (!_state.keepAliveResponse) {
      if (_state.session) {
        log('No keepalive response, but call active - deferring reconnect');
        commit(types.default.SET_PHONE_RECONNECT_AFTER_CALL, true);
        keepAliveSendOptions(_state, commit);
      } else if (_state.userAgent) {
        log('No keepalive response - forcing reconnect');
        dispatch('keepAliveStop');
        _state.userAgent?.stop();
      }
    } else if (_state.userAgent) {
      commit(types.default.SET_PHONE_RECONNECT_AFTER_CALL, false);
      await commit(types.default.SET_PHONE_KEEPALIVE_RESPONSE, null);
      keepAliveSendOptions(_state, commit);
    } else {
      log('User agent not connected, cannot send keepalive');
      await dispatch('keepAliveStop');
    }
  },
  keepAliveStop: async function keepAliveStop({ commit }) {
    commit(types.default.SET_PHONE_KEEPALIVE_INTERVAL, null);
  },
  audioCheckStart: async function audioCheckStart({ state: _state, commit }) {
    const interval = setInterval(async () => {
      if (_state.status !== 'ringing' && !_state.ringtone.paused) {
        _state.ringtone.pause();
      }
    }, 500);
    commit(types.default.SET_PHONE_AUDIO_CHECK_INTERVAL, interval);
  },
  audioCheckStop: async function audioCheckStop({ commit }) {
    commit(types.default.SET_PHONE_AUDIO_CHECK_INTERVAL, null);
  },
  fetchTurnCredentials: async function fetchTurnCredentials({ commit }) {
    const { data: turnConfig } =
      await UserPhoneCredentials.getTurnCredentials();
    if (turnConfig && turnConfig.urls) {
      log('TURN credentials fetched', turnConfig.urls);
      commit(types.default.SET_PHONE_TURN_CREDENTIALS, turnConfig);
    } else {
      commit(types.default.SET_PHONE_TURN_CREDENTIALS, null);
    }
  },
  turnCredentialsStart: async function turnCredentialsStart({
    dispatch,
    commit,
  }) {
    dispatch('fetchTurnCredentials');
    const interval = setInterval(async () => {
      await dispatch('fetchTurnCredentials');
    }, 3600 * 1000); // Refresh every hour
    commit(types.default.SET_PHONE_TURN_CREDENTIALS_INTERVAL, interval);
  },
  turnCredentialsStop: async function turnCredentialsStop({ commit }) {
    commit(types.default.SET_PHONE_TURN_CREDENTIALS_INTERVAL, null);
    commit(types.default.SET_PHONE_TURN_CREDENTIALS, null);
  },
  // Safety net for lost `phone_status.updated` websocket messages: while the
  // operator is connected and idle, periodically refetch their queue status so
  // a dropped broadcast can't leave the UI showing a stale status after a call.
  phoneStatusPollStart: async function phoneStatusPollStart({
    dispatch,
    commit,
  }) {
    const interval = setInterval(() => {
      dispatch('pollPhoneStatus');
    }, 10_000);
    commit(types.default.SET_PHONE_QUEUE_STATUS_POLL_INTERVAL, interval);
  },
  phoneStatusPollStop: async function phoneStatusPollStop({ commit }) {
    commit(types.default.SET_PHONE_QUEUE_STATUS_POLL_INTERVAL, null);
  },
  pollPhoneStatus: async function pollPhoneStatus({
    state: _state,
    dispatch,
    rootGetters,
  }) {
    // Only refresh while connected and idle. During a call the status is
    // 'speaking'/'dialing'/'ringing' and the queue status can't change, so a
    // poll would be wasted (and could race the in-call status transitions).
    if (_state.status !== 'connected') return;
    const agentId = rootGetters.getCurrentUserID;
    if (!agentId) return;
    try {
      const { data } = await UserPhoneCredentials.getPhoneStatus(agentId);
      dispatch('agents/setPhoneQueueStatuses', data, { root: true });
    } catch (e) {
      log('Phone status poll failed', e);
    }
  },
  // Returns false only when the browser explicitly denied mic permission
  // (NotAllowedError/SecurityError) — the one case where the agent could never
  // be heard on a call. Other failures (no device, device busy) and the
  // getUserMedia-unavailable case return true so they don't block going online.
  prewarmMic: async function prewarmMic({ state: _state, commit, dispatch }) {
    if (isStreamHealthy(_state.microphoneStream)) {
      return true;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      log('getUserMedia unavailable - skipping mic prewarm');
      return true;
    }
    // The companion extension pre-grants this origin's mic permission on load,
    // so getUserMedia resolves without a prompt — no page request needed here.
    try {
      const stream = await acquireMicStream();
      log('Mic prewarmed - APM converging on idle room');
      commit(types.default.SET_PHONE_MICROPHONE_STREAM, stream);
      // An agent stays online for days, so this stream has to survive sleep,
      // wake, and Chrome's audio service being restarted underneath it. When
      // any of those drops the capture the track goes 'ended' or 'muted' and
      // never recovers on its own — re-acquiring does.
      stream.getAudioTracks().forEach(track => {
        track.onended = () => dispatch('refreshMicrophoneStream', 'ended');
        track.onmute = () => dispatch('refreshMicrophoneStream', 'muted');
      });
      return true;
    } catch (e) {
      log('Mic prewarm failed', e);
      return !(e.name === 'NotAllowedError' || e.name === 'SecurityError');
    }
  },
  // Replaces a prewarmed stream that stopped delivering audio. Deliberately
  // re-checks after a beat: a track can report `muted` for a moment while the
  // OS hands the device over, and re-acquiring on that would be churn.
  refreshMicrophoneStream: async function refreshMicrophoneStream(
    { state: _state, commit, dispatch },
    reason
  ) {
    const track = _state.microphoneStream?.getAudioTracks()[0];
    if (!track) return;
    if (reason === 'muted') {
      await new Promise(resolve => {
        setTimeout(resolve, MIC_MUTE_SETTLE_MS);
      });
      const current = _state.microphoneStream?.getAudioTracks()[0];
      // Recovered on its own, or someone already replaced the stream.
      if (track !== current || !track.muted) return;
    }
    warn(`Microphone capture ${reason} — re-acquiring`);

    // Mid-call the existing hot-swap does the right thing: fresh stream,
    // replaceTrack on the live sender, mic meter rebuilt.
    if (_state.session?.connection) {
      await dispatch('setMicrophoneDevice', getSelectedMicId());
      return;
    }
    commit(types.default.SET_PHONE_MICROPHONE_STREAM, null);
    if (_state.userRequestedStatus === 'online') {
      await dispatch('prewarmMic');
    }
  },
  stopPrewarmMic: async function stopPrewarmMic({ commit }) {
    commit(types.default.SET_PHONE_MICROPHONE_STREAM, null);
  },
  setMicrophoneDevice: async function setMicrophoneDevice(
    { state: _state, commit, dispatch },
    deviceId
  ) {
    const normalized = deviceId || null;
    commit(types.default.SET_PHONE_SELECTED_MICROPHONE, normalized);
    await rememberDevice('audioinput', normalized);

    // Hot-swap the input on a live call without dropping it.
    if (_state.session?.connection) {
      try {
        const stream = await acquireMicStream();
        const [newTrack] = stream.getAudioTracks();
        const sender = _state.session.connection
          .getSenders()
          .find(s => s.track?.kind === 'audio');
        if (sender && newTrack) {
          newTrack.enabled = !_state.micMuted;
          await sender.replaceTrack(newTrack);
          // Mutation stops the previous stream's tracks for us.
          commit(types.default.SET_PHONE_MICROPHONE_STREAM, stream);
          // Rebuild streams.local from the (now swapped) senders so the
          // in-call mic-level meter follows the new device too.
          await dispatch('attachLocalMediaStream');
          log(`Switched microphone to ${normalized || 'default'} mid-call`);
          return;
        }
        stream.getTracks().forEach(t => t.stop());
      } catch (e) {
        log('Failed to switch microphone mid-call', e);
      }
      return;
    }

    // Idle: drop the prewarmed stream and re-acquire on the new device so the
    // next call starts on it.
    commit(types.default.SET_PHONE_MICROPHONE_STREAM, null);
    if (_state.userRequestedStatus === 'online') {
      await dispatch('prewarmMic');
    }
  },
  setSpeakerDevice: async function setSpeakerDevice({ commit }, deviceId) {
    const normalized = deviceId || null;
    commit(types.default.SET_PHONE_SELECTED_SPEAKER, normalized);
    await rememberDevice('audiooutput', normalized);
    // The actual routing (HTMLMediaElement.setSinkId) is applied by
    // PhoneAudioElements, which watches getSelectedSpeakerId.
  },
  // Checks the remembered devices against what is connected. Only a device that
  // came back under a new id (`relabeled`) writes. A device that is merely
  // missing (`stale`) keeps its place: calls already fall back to the system
  // default for it, and the choice is honoured again as soon as it is back.
  // Forgetting it used to throw away a deliberate pick on the first enumeration
  // that didn't list it — a Bluetooth headset switching to its hands-free
  // profile when its mic opens, a dock unplugged overnight — which agents saw
  // as "the audio settings don't save". `unknown` (enumeration hides ids until
  // mic permission is granted) never writes either.
  validateAudioDevices: async function validateAudioDevices({ dispatch }) {
    const [mics, speakers] = await Promise.all([
      availableDevices('audioinput'),
      availableDevices('audiooutput'),
    ]);
    const micId = getSelectedMicId();
    const speakerId = getSelectedSpeakerId();
    const microphone = resolveDeviceId(
      micId,
      mics,
      getStoredDeviceLabel('audioinput')
    );
    const speaker = resolveDeviceId(
      speakerId,
      speakers,
      getStoredDeviceLabel('audiooutput')
    );

    if (micId) trackDeviceResolution('audioinput', micId, microphone.status);
    if (speakerId) {
      trackDeviceResolution('audiooutput', speakerId, speaker.status);
    }

    if (microphone.status === 'relabeled') {
      warn(
        `Selected microphone ${deviceRef(micId)} is now ${deviceRef(microphone.id)} (same label) — updating the choice`
      );
      await dispatch('setMicrophoneDevice', microphone.id);
    }
    if (speaker.status === 'relabeled') {
      warn(
        `Selected speaker ${deviceRef(speakerId)} is now ${deviceRef(speaker.id)} (same label) — updating the choice`
      );
      await dispatch('setSpeakerDevice', speaker.id);
    }
    return { microphone: microphone.status, speaker: speaker.status };
  },
  // Cheap health check for the whole local audio path, run when the agent comes
  // back to the tab. Sleep/wake and an audio-service restart both leave a dead
  // capture behind without firing anything we listen to, and an agent who stays
  // online for days would otherwise only find out on their next call.
  revalidateAudioPath: async function revalidateAudioPath({
    state: _state,
    dispatch,
  }) {
    await dispatch('validateAudioDevices');
    if (_state.session) return;
    if (_state.microphoneStream && !isStreamHealthy(_state.microphoneStream)) {
      await dispatch('refreshMicrophoneStream', 'stale');
    }
  },
  // Fired (debounced) by PhoneAudioElements when the browser reports a device
  // being plugged in or removed.
  handleDeviceChange: async function handleDeviceChange({
    state: _state,
    commit,
    dispatch,
  }) {
    log('Audio devices changed');
    await dispatch('validateAudioDevices');

    // A device can disappear without the stored id changing (it was already the
    // system default, or the id survived the race with enumeration). Either way
    // a prewarmed stream on a gone device is dead weight.
    if (
      !_state.session &&
      _state.microphoneStream &&
      !isStreamHealthy(_state.microphoneStream)
    ) {
      log('Prewarmed microphone died with the device change — re-acquiring');
      commit(types.default.SET_PHONE_MICROPHONE_STREAM, null);
      if (_state.userRequestedStatus === 'online') {
        await dispatch('prewarmMic');
      }
      return;
    }

    // The remembered mic was missing, so the prewarm settled for the default
    // input. Now that it is back, move the idle stream onto it so the next call
    // uses the agent's choice. Never mid-call: yanking the input away from a
    // live conversation is worse than finishing it on the default.
    const selectedId = getSelectedMicId();
    const capturingId = _state.microphoneStream
      ?.getAudioTracks()[0]
      ?.getSettings?.().deviceId;
    if (
      _state.session ||
      !selectedId ||
      !capturingId ||
      capturingId === selectedId
    ) {
      return;
    }
    if ((await availableDeviceIds('audioinput')).includes(selectedId)) {
      log(
        `Selected microphone ${deviceRef(selectedId)} is back — moving the prewarmed stream onto it`
      );
      commit(types.default.SET_PHONE_MICROPHONE_STREAM, null);
      if (_state.userRequestedStatus === 'online') {
        await dispatch('prewarmMic');
      }
    }
  },
  reconnectIfNeeded: async function reconnectIfNeeded({
    dispatch,
    state: _state,
    commit,
  }) {
    if (_state.userRequestedStatus === 'offline') {
      log('reconnectIfNeeded: requested status is offline');
      return;
    }
    if (_state.pendingReconnect) {
      log('reconnectIfNeeded: reconnect already pending');
      return;
    }
    if (_state.connectAttemptsLeft === 0) {
      log(
        'reconnectIfNeeded: attempts exhausted - asking the operator to retry'
      );
      commit(types.default.SET_PHONE_CONNECTION_FAILED, true);
      return;
    }
    log('reconnectIfNeeded: will reconnect in a second');
    const timeoutId = setTimeout(async () => {
      if (
        _state.userRequestedStatus === 'online' &&
        _state.pendingReconnect === timeoutId
      ) {
        await commit(types.default.SET_PENDING_RECONNECT, false);
        await dispatch('connect');
      } else {
        log('reconnectIfNeeded: Skipping double reconnect');
      }
    }, 1000);
    await commit(types.default.SET_PENDING_RECONNECT, timeoutId);
  },
  disconnected: async function disconnected({
    state: _state,
    dispatch,
    commit,
  }) {
    window.postMessage(
      { type: 'PHONE_DISCONNECTED', timestamp: Date.now() },
      '*'
    );
    if (_state.session) {
      log('Disconnected, but call active - deferring reconnect');
      commit(types.default.SET_PHONE_RECONNECT_AFTER_CALL, true);
    } else {
      dispatch('cleanup');
    }
  },
  cleanup: async function cleanup({ state: _state, dispatch, commit }) {
    log('Cleaning up UA');
    _state.userAgent?.stop();
    _state.ringtone.pause();
    _state.connectDing.play();
    await commit(types.default.SET_PHONE_STATUS, 'offline');
    await commit(types.default.SET_PHONE_UA, null);
    await commit(types.default.SET_PHONE_RECONNECT_AFTER_CALL, false);
    await dispatch('registrationWatchdogStop');
    await dispatch('keepAliveStop');
    await dispatch('audioCheckStop');
    await dispatch('turnCredentialsStop');
    await dispatch('phoneStatusPollStop');
    await dispatch('reconnectIfNeeded');
  },
  disconnect: async function disconnect({ state: _state, commit, dispatch }) {
    await commit(types.default.SET_PENDING_RECONNECT, false);
    if (_state?.userAgent?.isConnected()) {
      log('Disconnecting from SIP server...');
      _state.userAgent?.stop();
    } else {
      dispatch('cleanup');
    }
  },
  // The Connect button stays clickable while the mic prewarms, which takes a
  // moment, so a double click (or a click during a reload's auto-connect)
  // started two of these: two getUserMedia calls and two `connect`s. A call
  // made while the mic prewarms now waits for that run, and its options are
  // ignored. The lock ends once `connect` has started the UA: claiming the
  // phone can take seconds, and a Retry or a new Connect after a failure in
  // that time must start a fresh attempt.
  requestConnect: async function requestConnect(context, options) {
    if (requestConnectInFlight) {
      await requestConnectInFlight;
      return;
    }
    requestConnectInFlight = prewarmAndConnect(context, options);
    let started = false;
    try {
      started = await requestConnectInFlight;
    } finally {
      requestConnectInFlight = null;
    }
    if (started) await claimPhone(context);
  },
  replayLastQueueAction: async function replayLastQueueAction({ rootGetters }) {
    const accountId = rootGetters.getCurrentAccountId;
    const map = readQueueActions(accountId);
    const entries = Object.entries(map);
    if (entries.length === 0) return;

    const agentId = rootGetters.getCurrentUserID;
    const callAllByStatus = status => dispatchQueueStatus({ agentId, status });
    const callQueueByStatus = (queueNumber, status) =>
      dispatchQueueStatus({ agentId, queueNumber, status });

    const agent = (rootGetters['agents/getAgents'] || []).find(
      a => a.id === agentId
    );
    const agentQueues = agent?.phone_queue_status || [];
    const uniqueStatuses = new Set(Object.values(map));
    const coversAllQueues =
      agentQueues.length > 0 &&
      agentQueues.every(q => map[q.number] !== undefined);

    try {
      if (uniqueStatuses.size === 1 && coversAllQueues) {
        const status = entries[0][1];
        await callAllByStatus(status);
      } else {
        await Promise.all(
          entries.map(async ([queueNumber, status]) => {
            try {
              await callQueueByStatus(queueNumber, status);
            } catch (e) {
              log(`Failed to replay queue ${queueNumber}/${status}`, e);
            }
          })
        );
      }
    } catch (e) {
      log('Failed to replay queue actions', e);
    }
  },
  // "Retry" from the connection-error dialog: two more attempts before we give
  // up and show it again. The flag is lowered here, synchronously, rather than
  // waiting for `requestConnect` — closing the dialog makes it emit `close`,
  // which must find the flag already down (see `dismissConnectionError`).
  retryConnect: async function retryConnect({ commit, dispatch }) {
    commit(types.default.SET_PHONE_CONNECTION_FAILED, false);
    commit(types.default.SET_PHONE_CONNECTION_ERROR_REASON, null);
    await dispatch('requestConnect', { attempts: RETRY_CONNECT_ATTEMPTS });
  },
  // Dismissing the dialog takes the phone properly offline (clearing the
  // "was online" session flag) so it doesn't auto-connect on the next reload
  // either. The `connectionFailed` guard doubles as the "did the operator
  // really dismiss this?" check: the dialog emits `close` on retry and on
  // programmatic closes too, and both lower the flag first.
  dismissConnectionError: async function dismissConnectionError({
    state: _state,
    commit,
    dispatch,
  }) {
    if (!_state.connectionFailed) return;
    commit(types.default.SET_PHONE_CONNECTION_FAILED, false);
    await dispatch('requestDisconnect');
  },
  requestDisconnect: async function requestDisconnect({
    commit,
    dispatch,
    rootGetters,
  }) {
    commit(types.default.SET_PHONE_CONNECTION_FAILED, false);
    commit(types.default.SET_PHONE_CONNECTION_ERROR_REASON, null);
    commit(types.default.SET_USER_PHONE_STATUS, 'offline');
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.USER_REQUESTED_PHONE_STATUS(
        rootGetters.getCurrentAccountId
      ),
      'offline'
    );
    // Let other tabs know the phone is free again.
    announcePhoneReleased(rootGetters.getCurrentAccountId);
    await dispatch('stopPrewarmMic');
    await dispatch('disconnect');
  },
  // Wire up cross-tab coordination once. Learns this tab's service-worker
  // client id, listens for ownership claims/releases/queries from other tabs,
  // and asks who currently owns the phone. Safe to call from multiple mounts.
  initTabCoordination: async function initTabCoordination({
    state: _state,
    commit,
    rootGetters,
  }) {
    if (tabCoordinationStarted) return;
    tabCoordinationStarted = true;

    // Subscribe and ask who owns the phone first, so we never miss an owner's
    // reply while the (possibly slow) client-id lookup is still resolving.
    subscribePhoneChannel(({ type, accountId, from, clientId }) => {
      if (accountId !== rootGetters.getCurrentAccountId) return;
      if (from === getPhoneTabId()) return;

      if (type === 'claim') {
        // Record the owner only while we are offline; a connected/connecting
        // tab keeps ownership (we never take over or get taken over).
        if (_state.status === 'offline') {
          commit(types.default.SET_PHONE_REMOTE_OWNER, {
            tabId: from,
            clientId,
          });
        }
      } else if (type === 'release') {
        if (_state.remotePhoneOwner?.tabId === from) {
          commit(types.default.SET_PHONE_REMOTE_OWNER, null);
        }
      } else if (type === 'query' && _state.status !== 'offline') {
        announcePhoneOwner(rootGetters.getCurrentAccountId, _state.ownClientId);
      }
    });

    queryPhoneOwner(rootGetters.getCurrentAccountId);

    // Resolve our own service-worker client id in the background (SW fallback
    // path only; the extension focus does not need it).
    commit(types.default.SET_PHONE_OWN_CLIENT_ID, await getOwnClientId());
  },
  // Focus the tab that currently owns the phone. Prefers the Chrome extension
  // (reliable chrome.tabs focus), falling back to the service worker. If it
  // can't be focused (closed, no extension/SW) clear the owner so the UI falls
  // back to a normal Connect button. Must run inside a user gesture.
  focusOwnerTab: async function focusOwnerTab({ state: _state, commit }) {
    const owner = _state.remotePhoneOwner;
    if (!owner) return false;
    let focused = false;
    if (_state.chromeExtensionAvailable) {
      focused = await focusPhoneTabViaExtension();
    }
    if (!focused && owner.clientId) {
      focused = await focusPhoneTab(owner.clientId);
    }
    if (!focused) {
      commit(types.default.SET_PHONE_REMOTE_OWNER, null);
    }
    return focused;
  },
  disconnectIfOnline: async function disconnectIfOnline({
    state: _state,
    dispatch,
  }) {
    if (_state.status === 'connected') {
      await dispatch('disconnect');
    }
  },
  acceptCall: async function acceptCall({ state: _state, dispatch }) {
    if (!_state.session) {
      log('No incoming call to accept');
      dispatch('callEnded', { reason: 'error' });
      return;
    }
    try {
      const sessionOptions = getSessionOptions(
        _state.turnCredentials,
        _state.microphoneStream
      );
      const { session } = _state;
      session.answer(sessionOptions);
      log('Incoming call answered');
      watchAnswer(session, dispatch);
      await dispatch('clearInboundCallNotification');
    } catch (e) {
      if (_state.session.isEstablished()) {
        log('Call already established, ignore error', e);
      } else {
        log('Failed to answer incoming call', e);
        terminateSafe(_state.session, 'Failed to terminate inbound');
        dispatch('callEnded', { reason: 'error' });
      }
    }
  },
  // Ends a session JsSIP has stopped driving. terminate() normally emits
  // 'failed', whose listener runs callEnded; if it throws or leaves the session
  // open, end the call here so it cannot keep holding the phone.
  abandonSession: async function abandonSession(
    { state: _state, dispatch },
    { session }
  ) {
    terminateSafe(session, 'Failed terminating abandoned session');
    if (!session.isEnded() && unref(_state.session) === session) {
      await dispatch('callEnded', {
        reason: 'failed',
        cause: JsSIP.C.causes.CONNECTION_ERROR,
      });
    }
  },
  dialingStarted: async function dialingStarted({ commit }) {
    let seconds = 0;
    const timer = setInterval(() => {
      seconds += 1;
      commit(types.default.SET_PHONE_DIALING_DURATION, seconds);
    }, 1000);
    commit(types.default.SET_PHONE_DIALING_TIMER, timer);
    commit(types.default.SET_PHONE_DIALING_DURATION, 0);
  },
  callStarted: async function callStarted({ state: _state, commit }) {
    _state.ringtone.pause();
    // Stop dialing timer when call starts
    commit(types.default.SET_PHONE_DIALING_TIMER, null);
    commit(types.default.SET_PHONE_DIALING_DURATION, null);

    let seconds = 0;
    const timer = setInterval(() => {
      seconds += 1;
      commit(types.default.SET_PHONE_CALL_DURATION, seconds);
    }, 1000);
    commit(types.default.SET_PHONE_CALL_TIMER, timer);
    commit(types.default.SET_PHONE_CALL_DURATION, 0);

    commit(types.default.SET_PHONE_CONNECTION_QUALITY_READY, false);
    commit(
      types.default.SET_PHONE_RTP_STATS_INTERVAL,
      startRtpStatsMonitor(_state.session, commit)
    );
    startConnectionQualityWarmup(_state.session, commit);
  },
  callEnded: async function callEnded(
    { state: _state, commit, dispatch },
    { reason, cause }
  ) {
    // PortSIP completes a transfer by hanging up both of our legs right after
    // it accepts the REFER, and never sends the NOTIFY that would reach
    // transferCompleted - so the call ending mid-completion is the transfer.
    // A rejected REFER ends the completion (transferFailed) before any hangup.
    if (reason === 'hangup' && _state.transferPhase === 'completing') {
      log('Call ended while completing the transfer - transfer done');
      reportTransfer(_state);
    }
    _state.ringtone.pause();
    let session = _state.session;

    // Keep microphoneStream tracks alive across calls — JsSIP won't stop
    // user-supplied mediaStream tracks. Re-enable in case session.mute() left
    // track.enabled=false. If stream died (permission revoked, device unplugged)
    // re-prewarm when operator still wants to be online.
    if (isStreamUsable(_state.microphoneStream)) {
      _state.microphoneStream.getTracks().forEach(t => {
        t.enabled = true;
      });
    } else {
      commit(types.default.SET_PHONE_MICROPHONE_STREAM, null);
      dispatch('prewarmMic');
    }

    await dispatch('clearInboundCallNotification');
    // Always notify Chrome extension that the call ended, in case
    // CLOSE_NOTIFICATION was missed or inboundCallNotification was already cleared
    window.postMessage(
      { type: 'CALL_ENDED', reason, timestamp: Date.now() },
      '*'
    );
    commit(types.default.SET_PHONE_STATUS, 'connected');
    commit(types.default.SET_PHONE_SESSION, {});
    commit(types.default.SET_PHONE_STREAMS, { local: null, remote: null });
    commit(types.default.SET_PHONE_ICE_CONNECTED, false);
    commit(types.default.SET_PHONE_CALL_DURATION, null);
    commit(types.default.SET_PHONE_DIALING_TIMER, null);
    commit(types.default.SET_PHONE_DIALING_DURATION, null);
    commit(types.default.SET_PHONE_CALL_TIMER, null);
    commit(types.default.SET_PHONE_RTP_STATS_INTERVAL, null);

    if (_state.disconnectedAt) {
      const duration = Math.round((new Date() - _state.disconnectedAt) / 1000);
      log(`Call ended while disconnected (disconnected for ${duration}s)`);
      _state.disconnectedAt = null;
    }

    commit(types.default.SET_PHONE_CONNECTION_QUALITY, null);
    commit(types.default.SET_PHONE_CONNECTION_RTT, null);
    commit(types.default.SET_PHONE_CONNECTION_JITTER, null);
    commit(types.default.SET_PHONE_CONNECTION_PACKET_LOSS, null);
    commit(types.default.SET_PHONE_CONNECTION_QUALITY_READY, false);
    commit(types.default.SET_PHONE_CONVERSATION, null);

    // The original call is gone, so a transfer leg still ringing has nowhere to
    // land — drop it rather than leave it ringing at the target. Clear the
    // reference first so its own 'failed' event is recognised as our teardown
    // (see setupTransferSession). Not routed through transferFailed: that would
    // put the status back to 'speaking' on a call that just ended.
    const pendingTransfer = _state.transferSession;
    clearTransferState(commit);
    if (pendingTransfer) {
      commit(types.default.SET_PHONE_TRANSFER_SESSION, null);
      terminateSafe(pendingTransfer, 'Terminating transfer leg after call end');
    }

    if (reason === 'failed') {
      // JsSIP relabels every getUserMedia failure on the call path as
      // USER_DENIED_MEDIA_ACCESS. Our resilient acquire already falls back off a
      // stale device id, so reaching here means the mic is genuinely blocked —
      // surface the audio settings so the operator can fix it.
      if (cause === JsSIP.C.causes.USER_DENIED_MEDIA_ACCESS) {
        log('Call failed on mic access — opening audio settings');
        emitter.emit(BUS_EVENTS.SHOW_PHONE_AUDIO_SETTINGS);
      }
      if (session.direction === 'incoming') {
        _state.hangupDing.play();
      } else {
        const failStatus = CAUSE_STATUS_MAP[cause] || 'call-failed';
        _state.busyTone.currentTime = 0;
        _state.busyTone.play();
        commit(types.default.SET_PHONE_STATUS, failStatus);
        setTimeout(() => {
          if (_state.status === failStatus) {
            commit(types.default.SET_PHONE_STATUS, 'connected');
          }
        }, 4000);
      }
    } else if (reason === 'hangup') {
      _state.hangupDing.play();
    }
    if (_state.reconnectAfterCall) {
      log('Call ended — reconnecting due to deferred issue');
      dispatch('cleanup');
    }
  },
  handleRemoteTrack: async function handleRemoteTrack(
    { state: _state, commit },
    { track, streams }
  ) {
    if (track.kind === 'audio' && streams.length > 0) {
      const remoteStream = streams[0];
      commit(types.default.SET_PHONE_STREAMS, {
        local: _state.streams.local,
        remote: remoteStream,
      });
    }
  },
  onIceConnected: async function onIceConnected({ state: _state, commit }) {
    log('ICE connected - media should be flowing');
    commit(types.default.SET_PHONE_ICE_CONNECTED, true);
    // Log the selected candidate pair so we know if we're using TURN relay or direct
    if (_state.session?.connection) {
      logStallDiagnostics(_state.session.connection);
    }

    // If we still don't have a remote stream at this point, try fallback
    if (!_state.streams.remote && _state.session?.connection) {
      log('ICE connected but no remote stream, attempting fallback');
      const remoteTracks = _state.session.connection
        .getReceivers()
        .map(receiver => receiver.track)
        .filter(t => t !== null);

      if (remoteTracks.length > 0) {
        log('Found remote tracks via fallback', remoteTracks);
        const remote = new MediaStream(remoteTracks);
        commit(types.default.SET_PHONE_STREAMS, {
          local: _state.streams.local,
          remote,
        });
      }
    }
  },
  // Attach only local media stream (called on accepted event)
  attachLocalMediaStream: async function attachLocalMediaStream({
    state: _state,
    commit,
  }) {
    if (!_state.session?.connection) {
      log('No connection available for local stream');
      return;
    }

    const localTracks = _state.session.connection
      .getSenders()
      .map(sender => sender.track)
      .filter(t => t !== null);

    let local = null;
    if (localTracks.length > 0) {
      log('attachLocalMediaStream: localTracks', localTracks);
      local = new MediaStream(localTracks);
    } else {
      log('No local stream found');
    }

    commit(types.default.SET_PHONE_STREAMS, {
      local,
      remote: _state.streams.remote,
    });
  },
  // Fallback: try to attach remote stream from receivers (called on confirmed event)
  attachRemoteMediaStreamFallback:
    async function attachRemoteMediaStreamFallback({ state: _state, commit }) {
      if (!_state.session?.connection) {
        log('No connection available for remote stream fallback');
        return;
      }

      const remoteTracks = _state.session.connection
        .getReceivers()
        .map(receiver => receiver.track)
        .filter(t => t !== null);

      if (remoteTracks.length > 0) {
        log('attachRemoteMediaStreamFallback: remoteTracks', remoteTracks);
        const remote = new MediaStream(remoteTracks);
        commit(types.default.SET_PHONE_STREAMS, {
          local: _state.streams.local,
          remote,
        });
      } else {
        log('No remote stream found in fallback - will retry on ICE connected');
      }
    },
  makeCall: async ({ state: _state }, { number: numberToDial }) => {
    if (_state.session) {
      warn('A call is already in progress, not dialing', numberToDial);
    } else if (_state.userAgent) {
      let processedNumber = cleanupOutboundNumber(numberToDial);
      const sessionOptions = getSessionOptions(
        _state.turnCredentials,
        _state.microphoneStream
      );
      log(`Calling ${processedNumber}`);
      _state.userAgent.call(processedNumber, sessionOptions);
    } else {
      log('User agent is not connected, ignoring makeCall request');
    }
  },
  hangup: async function hangup({ state: _state, dispatch }) {
    if (_state.session) {
      log('Hanging up the call');
      try {
        _state.session.terminate();
      } catch (e) {
        dispatch('callEnded', { reason: 'error' });
        log('Unable to hang up the call.', e);
      }
      _state.ringtone.pause();
    } else {
      log('No active call to hang up');
      dispatch('callEnded', { reason: 'error' });
    }
  },
  toggleHold: async function hold({ state: _state }) {
    if (!_state.session) {
      log('Call is not in progress');
      return;
    }
    if (_state.holdStatus === 'remote') {
      log('Call is held remotely cannot put on hold');
      return;
    }
    if (_state.holdStatus === 'local') {
      _state.session.unhold();
      log('Call off hold');
    } else {
      _state.session.hold();
      log('Call put on hold');
    }
  },
  toggleMute: async function toggleMute({ state: _state, commit }) {
    if (!_state.session) {
      log('Call is not in progress');
      return;
    }
    if (_state.micMuted) {
      _state.session.unmute();
      log('Microphone unmuted');
      commit(types.default.SET_PHONE_MIC_MUTED, false);
    } else {
      _state.session.mute();
      log('Microphone muted');
      commit(types.default.SET_PHONE_MIC_MUTED, true);
    }
  },
  toggleSilence: async function toggleSilence({ state: _state, commit }) {
    if (!_state.session) {
      log('Call is not in progress');
      return;
    }
    commit(types.default.SET_PHONE_SPEAKER_SILENT, !_state.speakerSilent);
  },
  transfer: async function transfer(
    { state: _state, commit, dispatch },
    { target, mode = 'blind' }
  ) {
    log(`Initiating ${mode} transfer to ${target}...`);
    commit(types.default.SET_PHONE_STATUS, 'transferring');
    commit(types.default.SET_PHONE_TRANSFER_STATE, {
      mode,
      phase: 'dialing',
      target,
    });
    // JsSIP's hold() returns false and does nothing when another re-INVITE is
    // in flight. Dialing anyway would leave the caller listening in on the
    // consultation, so treat a refused hold as a failed transfer.
    // (`isOnHold()` covers a 'hold' event we never saw.)
    if (!_state.holdStatus && !_state.session.isOnHold?.()?.local) {
      if (_state.session.hold() === false) {
        log('Could not put the call on hold - aborting the transfer');
        dispatch('transferFailed', {
          statusCode: 500,
          body: 'Transfer aborted: the call could not be put on hold',
        });
        return;
      }
    }
    try {
      let processedTarget = cleanupOutboundNumber(target);
      // Deliberately WITHOUT the prewarmed stream (unlike makeCall/acceptCall):
      // the original call is on hold for the duration of the transfer, and hold
      // disables `sender.track.enabled` on those very tracks — sharing them
      // would mute the transfer leg too. JsSIP acquires its own stream here,
      // pinned with `ideal` so a stale device id degrades instead of throwing.
      const sessionOptions = getSessionOptions(_state.turnCredentials);
      log(`Dialing transfer target: ${target}`);
      _state.userAgent.call(processedTarget, sessionOptions);
    } catch (error) {
      log('Failed to initiate transfer call', error);
      dispatch('transferFailed', {
        statusCode: 500,
        body: `Transfer call failed: ${error.message || error}`,
      });
    }
  },
  cancelTransfer: async function cancelTransfer({ state: _state, dispatch }) {
    if (_state.status !== 'transferring') {
      log('Cancel transfer ignored - no transfer in progress');
      return;
    }
    log('Transfer ended by agent - resuming original call');
    await dispatch('transferFailed', {
      statusCode: 487,
      body: 'Transfer cancelled',
    });
  },
  setupTransferSession: async function setupTransferSession(
    { state: _state, commit, dispatch },
    { session: transferSession }
  ) {
    commit(types.default.SET_PHONE_TRANSFER_SESSION, transferSession);
    // True once we tore this leg down ourselves (cancel / completion / a
    // failure we already handled). terminate() makes JsSIP emit 'failed' on the
    // session, so without this the handlers below re-enter transferFailed and
    // produce a bogus 500 plus a second unhold of the original call.
    const isStale = () => _state.transferSession !== transferSession;
    // The agent has to hear this leg to consult on it. The call's own
    // setupPeerConnectionListeners can't be reused: its handleRemoteTrack /
    // onIceConnected write into state.streams, which still belongs to the
    // (held) original call.
    const attachLegAudio = source => {
      if (isStale()) return;
      // Only the consultation flow listens to this leg. In the direct flow the
      // call is handed over the moment it answers, and the agent heard nothing
      // before this feature existed.
      if (_state.transferMode !== 'consult') return;
      const remoteTracks = (transferSession.connection?.getReceivers() ?? [])
        .map(receiver => receiver.track)
        .filter(track => track?.kind === 'audio');
      if (!remoteTracks.length) {
        log(`No remote track on the transfer leg yet (${source})`);
        return;
      }
      log(`Attaching transfer leg audio (${source})`);
      commit(
        types.default.SET_PHONE_TRANSFER_REMOTE_STREAM,
        new MediaStream(remoteTracks)
      );
    };
    // Outgoing sessions already own their RTCPeerConnection by the time
    // newRTCSession fires (same assumption as setupSessionListeners).
    transferSession.connection?.addEventListener('track', () =>
      attachLegAudio('track event')
    );
    transferSession.on('confirmed', () => {
      if (!_state.transferRemoteStream) attachLegAudio('confirmed fallback');
    });
    transferSession.on('accepted', e => {
      if (isStale()) {
        log('Transfer session no longer active, ignoring accepted event');
        return;
      }
      if (_state.transferMode === 'consult') {
        log('Transfer target answered - consulting', e);
        commit(types.default.SET_PHONE_TRANSFER_STATE, { phase: 'consulting' });
        return;
      }
      // let the session stabilize before completing the transfer
      setTimeout(() => {
        if (isStale()) {
          log('Transfer session no longer active, skipping completion');
          return;
        }
        log('Transfer target answered - completing transfer', e);
        dispatch('completeAttendedTransfer');
      }, 500);
    });
    transferSession.on('ended', e => {
      log('Transfer call ended', e);
      if (isStale()) return;
      // Completion in flight: the PBX hangs this leg up as part of the
      // Replaces, and it can get here before the REFER's own NOTIFY does.
      // Let the REFER decide the outcome.
      if (_state.transferPhase === 'completing') {
        log('Transfer leg ended while completing - waiting for the REFER');
        return;
      }
      // The target hung up on us (typically after a consultation). Nothing
      // else would move the phone off 'transferring'.
      dispatch('transferFailed', {
        statusCode: 200,
        body: 'Transfer target hung up',
      });
    });
    transferSession.on('failed', e => {
      if (isStale()) {
        log('Transfer session already torn down, ignoring failed event', e);
        return;
      }
      log('Transfer target call failed', e);
      dispatch('transferFailed', {
        statusCode: e.status_code || 500,
        body: `Transfer call failed: ${e.reason_phrase || 'unknown reason'}`,
      });
    });
  },
  // The "complete the transfer" button of a consultation. Guarded so it can
  // only fire while the two agents are actually connected.
  completeConsultTransfer: async function completeConsultTransfer({
    state: _state,
    dispatch,
  }) {
    if (_state.transferPhase !== 'consulting') {
      log(`Complete transfer ignored - phase is ${_state.transferPhase}`);
      return;
    }
    await dispatch('completeAttendedTransfer');
  },
  completeAttendedTransfer: async function completeAttendedTransfer({
    state: _state,
    commit,
    dispatch,
  }) {
    // A second REFER would be answered with a failure and tear down a transfer
    // that is already on its way.
    if (_state.transferPhase === 'completing') {
      log('Transfer completion already in progress');
      return;
    }
    if (!_state.session || !_state.transferSession?.isEstablished()) {
      log('Cannot complete transfer - invalid state');
      dispatch('transferFailed', {
        statusCode: 500,
        body: `Transfer call failed: Invalid state`,
      });
      return;
    }
    try {
      log('Completing attended transfer...');
      commit(types.default.SET_PHONE_TRANSFER_STATE, { phase: 'completing' });
      const referSubscriber = _state.session.refer(
        _state.transferSession.remote_identity.uri.toString(),
        { replaces: _state.transferSession }
      );
      referSubscriber.on('accepted', () => {
        log('Attended transfer completed successfully');
        dispatch('transferCompleted');
      });
      referSubscriber.on('failed', e => {
        log('Attended transfer failed', e);
        dispatch('transferFailed', {
          statusCode: e.status_code || 500,
          body: e.reason_phrase || 'Transfer completion failed',
        });
      });
      // 'failed' only ever comes from a NOTIFY. A REFER the other side rejects
      // outright (4xx/5xx), a transport error, a timeout or a dialog error all
      // emit 'requestFailed' instead - without this the transfer would hang in
      // 'completing' forever with the caller on hold.
      referSubscriber.on('requestFailed', e => {
        log('Attended transfer request failed', e);
        dispatch('transferFailed', {
          statusCode: e?.response?.status_code || 500,
          body: `Transfer request failed: ${e?.cause || 'unknown reason'}`,
        });
      });
      // Last resort: a REFER that is accepted but never produces a final
      // NOTIFY leaves no event at all to act on.
      commit(
        types.default.SET_PHONE_TRANSFER_TIMER,
        setTimeout(
          () => dispatch('transferCompletionTimedOut'),
          TRANSFER_COMPLETION_TIMEOUT_MS
        )
      );
    } catch (error) {
      log('Error completing attended transfer', error);
      dispatch('transferFailed', {
        statusCode: 500,
        body: `Transfer completion failed: ${error.message || error}`,
      });
    }
  },
  transferCompletionTimedOut: async function transferCompletionTimedOut({
    state: _state,
    dispatch,
  }) {
    if (_state.transferPhase !== 'completing') {
      return;
    }
    log('Transfer completion produced no outcome - resuming the original call');
    await dispatch('transferFailed', {
      statusCode: 408,
      body: 'Transfer completion timed out',
    });
  },
  transferCompleted: async function transferCompleted({
    dispatch,
    commit,
    state: _state,
  }) {
    log('Transfer completed');
    // Before the transfer state and the call are cleared below.
    reportTransfer(_state);
    clearTransferState(commit);
    const transferSession = _state.transferSession;
    if (transferSession) {
      // Clear the reference before terminating so the session's own 'failed'
      // event is recognised as our own teardown (see setupTransferSession).
      commit(types.default.SET_PHONE_TRANSFER_SESSION, null);
      terminateSafe(transferSession, 'Terminating completed transfer session');
    }
    await dispatch('hangup');
  },
  transferFailed: async function transferFailed(
    { commit, state: _state },
    { statusCode, body } = {}
  ) {
    log(`Transfer failed: ${statusCode} - ${body}`);
    clearTransferState(commit);
    const transferSession = _state.transferSession;
    if (transferSession) {
      // Clear the reference before terminating so the session's own 'failed'
      // event is recognised as our own teardown (see setupTransferSession).
      commit(types.default.SET_PHONE_TRANSFER_SESSION, null);
      terminateSafe(transferSession, 'Terminating failed transfer session');
    }
    log('Resuming original call after transfer failure');
    // unhold() refuses in the same cases as hold(); one retry covers a
    // re-INVITE that was still in flight. holdStatus stays 'local' if it never
    // lands, so the agent can still resume it from the hold button.
    if (_state.session?.unhold() === false) {
      log('Could not resume the call yet - retrying shortly');
      setTimeout(() => {
        if (_state.session && _state.holdStatus === 'local') {
          _state.session.unhold();
        }
      }, 1000);
    }
    commit(types.default.SET_PHONE_STATUS, 'speaking');
  },
  findContact: async ({ state: _state, dispatch, commit }) => {
    const callerPhoneNumber = await _state.otherPartyNumber;
    let contact = await dispatch('contacts/filterByNumber', callerPhoneNumber, {
      root: true,
    });
    if (contact) {
      log('Using existing contact', contact);
      commit(types.default.SET_PHONE_CONTACT, contact);
    }
  },
  setupConversation: async (
    { state: _state, dispatch },
    { attempt, callInfo, sessionId }
  ) => {
    if (_state.otherPartyNumber.length <= 5) {
      log('Extension call - no conversation UI action required');
      return;
    }
    // On each attempt, prefer params from prior retry, then fall back to state.
    // This ensures retries survive callEnded clearing the state, while also
    // picking up values that arrive after attempt 1 (e.g. X-Session-ID).
    const resolvedCallInfo = callInfo?.conversation_id
      ? callInfo
      : _state?.callInfo;
    const resolvedSessionId = sessionId || _state?.sessionId;

    if (resolvedCallInfo?.conversation_id) {
      const { data: conversationFromHeader } = await ConversationApi.show(
        resolvedCallInfo.conversation_id
      );
      log('Open conversation from call info');
      dispatch('openConversation', conversationFromHeader);
      return;
    }
    let conversationFromSession =
      await conversationFromSessionId(resolvedSessionId);
    if (conversationFromSession) {
      log('Open conversation from session ID');
      dispatch('openConversation', conversationFromSession);
      return;
    }
    if (attempt && attempt < 10) {
      log(
        'Conversation not found yet.',
        attempt,
        resolvedCallInfo,
        resolvedSessionId
      );
      setTimeout(() => {
        dispatch('setupConversation', {
          attempt: attempt + 1,
          callInfo: resolvedCallInfo,
          sessionId: resolvedSessionId,
        });
      }, 2000);
      return;
    }
    log('Giving up on finding conversation');
  },
  openTransferredConversation: async ({ dispatch }, { conversationId }) => {
    log('Call transferred to me - opening its conversation', conversationId);
    try {
      const { data: conversation } = await ConversationApi.show(conversationId);
      dispatch('openConversation', conversation);
    } catch (error) {
      log('Failed to open the transferred conversation', error);
    }
  },
  openConversation: async ({ commit }, conversation) => {
    await commit(types.default.SET_PHONE_CONVERSATION, conversation);
    log(
      'Opening conversation - start',
      conversation.account_id,
      conversation.id,
      window.location.href
    );
    emitter.emit(BUS_EVENTS.PHONE_OPEN_CONVERSATION, {
      accountId: conversation.account_id,
      conversationId: conversation.id,
    });
    log(
      'Opening conversation - end',
      conversation.account_id,
      conversation.id,
      window.location.href
    );
  },
  showInboundCallPushNotification: async ({ state: _state, commit }) => {
    const callerNumber = _state.otherPartyNumber;
    const callerId = _state.callerId;
    let callerName = _state.contact?.name;

    if (_state.chromeExtensionAvailable) {
      showInboundCallExtensionNotification(callerId, callerNumber, callerName);
      commit(types.default.SET_PHONE_INBOUND_CALL_NOTIFICATION, 'EXTENSION');
    } else {
      if (callerId) {
        callerName = callerId;
      }
      const notification = showInboundCallPushNotification(
        callerNumber,
        callerName
      );
      if (notification) {
        commit(types.default.SET_PHONE_INBOUND_CALL_NOTIFICATION, notification);
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
        notification.onclose = () => {
          commit(types.default.SET_PHONE_INBOUND_CALL_NOTIFICATION, null);
        };
      }
    }
  },
  clearInboundCallNotification: async function clearInboundCallNotification({
    state: _state,
    commit,
  }) {
    if (_state.inboundCallNotification === 'EXTENSION') {
      window.postMessage(
        { type: 'CLOSE_NOTIFICATION', timestamp: Date.now() },
        '*'
      );
    } else if (_state.inboundCallNotification) {
      _state.inboundCallNotification.close();
    }
    commit(types.default.SET_PHONE_INBOUND_CALL_NOTIFICATION, null);
  },
  setupChromeExtensionListeners: async function setupChromeExtensionListeners({
    dispatch,
    rootGetters,
  }) {
    window.addEventListener('jarabot:phone:accept-call', () => {
      log('Chrome extension: accepting call');
      dispatch('acceptCall');
    });
    window.addEventListener('jarabot:phone:decline-call', () => {
      log('Chrome extension: declining call');
      dispatch('hangup');
    });
    window.addEventListener('jarabot:phone:dial-number', event => {
      const { number } = event.detail;
      log('Chrome extension: dial number', number);
      const account = rootGetters['accounts/getCurrentAccount'];
      if (account.dialing_mode === 'direct') {
        dispatch('makeCall', { number });
      } else {
        emitter.emit(BUS_EVENTS.SHOW_PHONE_DIALER, number);
      }
    });
  },
  checkChromeExtension: async function checkChromeExtension({
    state: _state,
    commit,
    dispatch,
  }) {
    if (_state.chromeExtensionAvailable) return;
    commit(types.default.SET_CHROME_EXTENSION_AVAILABLE, false);
    const handleExtensionPong = event => {
      if (event.data.type === 'EXTENSION_PONG') {
        log('Chrome extension detected');
        commit(types.default.SET_CHROME_EXTENSION_AVAILABLE, true);
        dispatch('setupChromeExtensionListeners');
        window.removeEventListener('message', handleExtensionPong);
      }
    };
    window.addEventListener('message', handleExtensionPong);
    window.postMessage({ type: 'EXTENSION_PING', timestamp: Date.now() }, '*');
  },
  sendDTMF: async function sendDTMF({ state: _state }, { tone, options = {} }) {
    if (!_state.session || !_state.session.isEstablished()) {
      log('Cannot send DTMF - no active session');
      return false;
    }

    try {
      const defaultOptions = {
        duration: 100,
        interToneGap: 50,
      };
      const dtmfOptions = { ...defaultOptions, ...options };

      log(`Sending DTMF tone: ${tone}`);
      _state.session.sendDTMF(tone, dtmfOptions);
      return true;
    } catch (error) {
      log('Failed to send DTMF tone', error);
      return false;
    }
  },
  setPhoneControlsVisible: async function setPhoneControlsVisible(
    { commit },
    visible
  ) {
    commit(types.default.SET_PHONE_CONTROLS_VISIBLE, visible);
  },
};

export const mutations = {
  [types.default.SET_PHONE_UA](_state, ua) {
    if (ua) {
      _state.userAgent = shallowRef(ua);
    } else {
      _state.userAgent = null;
    }
  },
  [types.default.SET_PHONE_SESSION](_state, { session, request }) {
    _state.session = shallowRef(session);
    _state.request = shallowRef(request);
    _state.callInfo = parseCallInfo(request);
    _state.sessionId = request?.getHeader('X-Session-ID');
    _state.micMuted = false;
    _state.speakerSilent = false;
    _state.holdStatus = null;
    _state.contact = null;
    if (session) {
      _state.callerId = parseCallerId(session);
      _state.otherPartyNumber = parseRemotePartyNumber(
        _state.callInfo,
        session,
        request
      );
    }
  },
  [types.default.SET_PHONE_SESSION_ID](_state, { sessionId }) {
    _state.sessionId = sessionId;
  },
  [types.default.SET_PHONE_KEEPALIVE_INTERVAL](_state, interval) {
    if (_state.keepAliveInterval) {
      clearInterval(_state.keepAliveInterval);
    }
    _state.keepAliveInterval = interval;
  },
  [types.default.SET_PHONE_KEEPALIVE_RESPONSE](_state, response) {
    _state.keepAliveResponse = response;
  },
  [types.default.SET_PHONE_RECONNECT_AFTER_CALL](_state, value) {
    _state.reconnectAfterCall = value;
  },
  [types.default.SET_PHONE_AUDIO_CHECK_INTERVAL](_state, interval) {
    if (_state.audioCheckInterval) {
      clearInterval(_state.audioCheckInterval);
    }
    _state.audioCheckInterval = interval;
  },
  [types.default.SET_USER_PHONE_STATUS](_state, status) {
    _state.userRequestedStatus = status;
  },
  [types.default.SET_PENDING_RECONNECT](_state, value) {
    if (_state.pendingReconnect) {
      clearTimeout(_state.pendingReconnect);
    }
    _state.pendingReconnect = value;
  },
  [types.default.SET_PHONE_CONNECT_ATTEMPTS_LEFT](_state, value) {
    _state.connectAttemptsLeft = value;
  },
  [types.default.SET_PHONE_CONNECTION_FAILED](_state, value) {
    _state.connectionFailed = value;
  },
  [types.default.SET_PHONE_CONNECTION_ERROR_REASON](_state, reason) {
    _state.connectionErrorReason = reason;
  },
  [types.default.SET_PHONE_REGISTRATION_TIMER](_state, timer) {
    if (_state.registrationTimer) {
      clearTimeout(_state.registrationTimer);
    }
    _state.registrationTimer = timer;
  },
  [types.default.SET_PHONE_STATUS](_state, status) {
    if (status === 'dialing' && _state.status !== 'connected') {
      // ignore switch to dialing if already speaking
      log(`Phone status ignored: ${_state.status} -> dialing`);
    } else {
      log(`Phone status changed: ${_state.status} -> ${status}`);
      _state.status = status;
    }
  },
  [types.default.SET_PHONE_STREAMS](_state, streams) {
    _state.streams = shallowRef(streams);
  },
  [types.default.SET_PHONE_ICE_CONNECTED](_state, connected) {
    _state.iceConnected = connected;
  },
  [types.default.SET_PHONE_CALL_TIMER](_state, timer) {
    if (_state.callTimer) {
      clearInterval(_state.callTimer);
    }
    _state.callTimer = timer;
  },
  [types.default.SET_PHONE_CALL_DURATION](_state, seconds) {
    _state.callDuration = seconds;
  },
  [types.default.SET_PHONE_DIALING_TIMER](_state, timer) {
    if (_state.dialingTimer) {
      clearInterval(_state.dialingTimer);
    }
    _state.dialingTimer = timer;
  },
  [types.default.SET_PHONE_DIALING_DURATION](_state, seconds) {
    _state.dialingDuration = seconds;
  },
  [types.default.SET_PHONE_HOLD](_state, status) {
    _state.holdStatus = status;
  },
  [types.default.SET_PHONE_SPEAKER_SILENT](_state, status) {
    _state.speakerSilent = status;
  },
  [types.default.SET_PHONE_MIC_MUTED](_state, status) {
    _state.micMuted = status;
  },
  [types.default.SET_PHONE_CONTACT](_state, contact) {
    _state.contact = contact;
  },
  [types.default.SET_PHONE_CONVERSATION](_state, conversation) {
    _state.conversation = conversation;
  },
  [types.default.SET_PHONE_INBOUND_CALL_NOTIFICATION](_state, notification) {
    _state.inboundCallNotification = notification;
  },
  [types.default.SET_CHROME_EXTENSION_AVAILABLE](_state, available) {
    _state.chromeExtensionAvailable = available;
  },
  [types.default.SET_PHONE_TRANSFER_SESSION](_state, session) {
    _state.transferSession = session ? shallowRef(session) : null;
  },
  // Partial merge: callers set only the fields they know about (the phase
  // moves on its own several times within one chosen mode).
  [types.default.SET_PHONE_TRANSFER_STATE](_state, transferState) {
    const { mode, phase, target } = transferState ?? {};
    if (mode !== undefined) _state.transferMode = mode;
    if (phase !== undefined) _state.transferPhase = phase;
    if (target !== undefined) _state.transferTarget = target;
  },
  [types.default.SET_PHONE_TRANSFER_REMOTE_STREAM](_state, stream) {
    _state.transferRemoteStream = stream ? shallowRef(stream) : null;
  },
  [types.default.SET_PHONE_TRANSFER_TIMER](_state, timer) {
    if (_state.transferCompletionTimer) {
      clearTimeout(_state.transferCompletionTimer);
    }
    _state.transferCompletionTimer = timer;
  },
  [types.default.SET_PHONE_CONTROLS_VISIBLE](_state, visible) {
    _state.phoneControlsVisible = visible;
  },
  [types.default.SET_PHONE_TURN_CREDENTIALS](_state, credentials) {
    _state.turnCredentials = credentials;
  },
  [types.default.SET_PHONE_TURN_CREDENTIALS_INTERVAL](_state, interval) {
    if (_state.turnCredentialsInterval) {
      clearInterval(_state.turnCredentialsInterval);
    }
    _state.turnCredentialsInterval = interval;
  },
  [types.default.SET_PHONE_RTP_STATS_INTERVAL](_state, interval) {
    if (_state.rtpStatsInterval) {
      clearInterval(_state.rtpStatsInterval);
    }
    _state.rtpStatsInterval = interval;
  },
  [types.default.SET_PHONE_QUEUE_STATUS_POLL_INTERVAL](_state, interval) {
    if (_state.phoneQueueStatusPollInterval) {
      clearInterval(_state.phoneQueueStatusPollInterval);
    }
    _state.phoneQueueStatusPollInterval = interval;
  },
  [types.default.SET_PHONE_CONNECTION_QUALITY](_state, quality) {
    _state.connectionQuality = quality;
  },
  [types.default.SET_PHONE_CONNECTION_RTT](_state, rtt) {
    _state.connectionRtt = rtt;
  },
  [types.default.SET_PHONE_CONNECTION_JITTER](_state, jitter) {
    _state.connectionJitter = jitter;
  },
  [types.default.SET_PHONE_CONNECTION_PACKET_LOSS](_state, loss) {
    _state.connectionPacketLoss = loss;
  },
  [types.default.SET_PHONE_CONNECTION_QUALITY_READY](_state, ready) {
    _state.connectionQualityReady = ready;
  },
  [types.default.SET_PHONE_MICROPHONE_STREAM](_state, stream) {
    if (_state.microphoneStream && _state.microphoneStream !== stream) {
      _state.microphoneStream.getTracks().forEach(t => {
        t.onended = null;
        t.onmute = null;
        t.stop();
      });
    }
    _state.microphoneStream = stream ? shallowRef(stream) : null;
    _state.microphoneStreamAcquiredAt = stream ? Date.now() : null;
  },
  [types.default.SET_PHONE_REMOTE_OWNER](_state, owner) {
    _state.remotePhoneOwner = owner;
  },
  [types.default.SET_PHONE_OWN_CLIENT_ID](_state, clientId) {
    _state.ownClientId = clientId;
  },
  [types.default.SET_PHONE_SELECTED_MICROPHONE](_state, deviceId) {
    _state.selectedMicrophoneId = deviceId || null;
  },
  [types.default.SET_PHONE_SELECTED_SPEAKER](_state, deviceId) {
    _state.selectedSpeakerId = deviceId || null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
